import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import beeLogo from "@assets/logo-bee-yellow_1778007378692.png";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-rotate";
import { BakedHeatLayer } from "@/components/BakedHeatLayer";
import { MapBearingSync } from "@/components/MapBearingSync";
import { MapMarker } from "@/components/MapMarker";
import { UserHeadingCone } from "@/components/UserHeadingCone";
import { useDeviceHeading } from "@/hooks/use-device-heading";
import { VenueDetailDrawer } from "@/components/VenueDetailDrawer";
import { FullscreenVibeViewer } from "@/components/FullscreenVibeViewer";
import { Navigation } from "@/components/Navigation";
import { CardPhotoBg } from "@/components/CardPhotoBg";
import { useVenues, useSponsoredVenues, useHeatVenues, type MapBounds } from "@/hooks/use-venues";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { useLocation, useSearch } from "wouter";
import { getCapacityColour, getCapacityLabel as sharedGetCapacityLabel, getCapacityLabelTextClass, getCapacityLabelDotClass } from "@/lib/capacity-utils";
import { matchNeighbourhoods, haversineDistance, LONDON_NEIGHBOURHOODS, type LondonNeighbourhood } from "@/lib/london-neighbourhoods";
import { useFriendMapPins } from "@/hooks/use-friends";
import { ProfilePage } from "@/pages/ProfilePage";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { SettingsPanel } from "@/components/SettingsPanel";
import { Search, Loader2, Navigation as NavIcon, SlidersHorizontal, Check, MapPin, Users, GlassWater, Utensils, Coffee, Music, X, User, LogOut, Shield, Briefcase, ChevronRight, Heart, Play, ThumbsUp, ThumbsDown, Video, ArrowLeft, Flame, CalendarDays, Compass, Settings as SettingsIcon } from "lucide-react";
import { motion, AnimatePresence, useDragControls, type PanInfo } from "framer-motion";

const PAGE_SWAP_TRANSITION = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 24 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { timeAgo } from "@/lib/time-ago";
import type { Favourite, Venue } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import "leaflet/dist/leaflet.css";

const CARTO_API_KEY = import.meta.env.VITE_CARTO_API_KEY as string;
if (!CARTO_API_KEY) {
  throw new Error("VITE_CARTO_API_KEY is required");
}
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Enables leaflet-rotate map bearing support. The map rotates naturally from a
// two-finger touch gesture (touchRotate); shift+drag rotates on desktop.
const MAP_ROTATE_OPTIONS = {
  rotate: true,
  rotateControl: false,
  touchRotate: true,
  shiftKeyRotate: true,
  bearing: 0,
} as any;

function RecenterMap({ lat, lng, zoom, version, onComplete }: { lat: number, lng: number, zoom?: number, version: number, onComplete?: () => void }) {
  const map = useMap();
  const lastVersion = useRef(version);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  useEffect(() => {
    if (version !== lastVersion.current) {
      lastVersion.current = version;
      map.flyTo([lat, lng], zoom ?? 13, { duration: 1.5 });
      if (onCompleteRef.current) {
        const cb = onCompleteRef.current;
        const handler = () => {
          map.off("moveend", handler);
          cb();
        };
        map.on("moveend", handler);
      }
    }
  }, [lat, lng, zoom, version, map]);
  return null;
}

const EVENT_GENRES = ["All", "Electronic", "Live Music", "Hip-Hop", "Jazz", "House", "Techno"] as const;
type EventGenre = typeof EVENT_GENRES[number];

const GENRE_KEYWORDS: Record<Exclude<EventGenre, "All">, string[]> = {
  "Electronic": ["electronic", "techno", "house", "trance", "edm", "dnb", "drum & bass", "drum and bass", "garage", "rave"],
  "Live Music": ["live", "band", "acoustic", "concert", "gig", "performance", "singer", "songwriter"],
  "Hip-Hop": ["hip-hop", "hip hop", "hiphop", "rap", "r&b", "rnb", "trap", "grime"],
  "Jazz": ["jazz", "blues", "soul", "funk"],
  "House": ["house", "deep house", "tech house", "afro house"],
  "Techno": ["techno", "minimal", "industrial"],
};

function matchesGenre(haystack: string, genre: EventGenre): boolean {
  if (genre === "All") return true;
  const h = haystack.toLowerCase();
  return GENRE_KEYWORDS[genre].some(k => h.includes(k));
}

type EventListItem = {
  id: number;
  venueId: number | null;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string | null;
  imageUrl: string | null;
  ticketUrl: string | null;
  source: "business" | "resident_advisor";
  externalId: string | null;
  raVenueName: string | null;
  raVenueAddress: string | null;
  venueName: string | null;
  venueLat: number | null;
  venueLng: number | null;
};

function formatEventDayBadge(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  if (isToday) return "Tonight";
  if (isTomorrow) return "Tomorrow";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function formatEventTimeShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true })
    .toLowerCase().replace(/\s+/g, "");
}

function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Open a URL in the user's default browser. On an installed iOS standalone PWA,
// window.open() opens an in-app web view; a real anchor click (within the user
// gesture) escapes to the system default browser instead.
function openExternal(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function EventCardV4({
  ev, dist, onTap, testIdPrefix,
}: {
  ev: EventListItem;
  dist: number | null;
  onTap: () => void;
  testIdPrefix: string;
}) {
  const venueLabel = ev.venueName ?? ev.raVenueName ?? "";
  const isTappable = ev.venueId !== null && ev.venueLat !== null && ev.venueLng !== null;
  const dayBadge = formatEventDayBadge(ev.startTime);
  const timeShort = formatEventTimeShort(ev.startTime);
  return (
    <div
      data-testid={`${testIdPrefix}-${ev.id}`}
      onClick={onTap}
      className={`rounded-2xl overflow-hidden mb-3 bg-zinc-900 border border-zinc-800 transition-all ${isTappable || ev.ticketUrl ? "hover:border-zinc-700 cursor-pointer active:scale-[0.99]" : ""}`}
    >
      <div className="relative h-[130px] flex items-end">
        {ev.imageUrl ? (
          <img src={ev.imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,#1f1f1f_0,#1f1f1f_4px,#272727_4px,#272727_12px)]" />
        )}
        <div className="absolute top-3 left-3 bg-black/65 backdrop-blur-sm rounded-lg px-2.5 py-1">
          <span className="text-[12px] font-bold text-white">{dayBadge}</span>
        </div>
        {ev.source === "resident_advisor" && (
          <div
            data-testid={`${testIdPrefix}-source-${ev.id}`}
            className="absolute top-3 right-3 bg-primary rounded-md px-2 py-0.5"
          >
            <span className="text-[11px] font-extrabold text-primary-foreground">RA</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />
        <div className="relative px-3.5 pb-3">
          <h3 className="text-[17px] font-display font-extrabold text-white leading-tight line-clamp-2">{ev.title}</h3>
        </div>
      </div>
      <div className="px-3.5 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {venueLabel && (
            <div className="flex items-center gap-1.5 mb-0.5 min-w-0">
              <MapPin className="w-3 h-3 text-zinc-500 shrink-0" />
              <span className="text-[13px] text-zinc-300 font-semibold truncate">{venueLabel}</span>
              {dist !== null && (
                <span className="text-[12px] text-zinc-500 shrink-0">· {dist.toFixed(1)}km</span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <CalendarDays className="w-3 h-3 text-zinc-500 shrink-0" />
            <span className="text-[12px] text-zinc-500">{timeShort}</span>
          </div>
        </div>
        {ev.ticketUrl ? (
          <a
            data-testid={`${testIdPrefix}-tickets-${ev.id}`}
            href={ev.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center bg-primary text-primary-foreground rounded-full px-4 py-2 text-[13px] font-extrabold hover:bg-primary/90 transition-colors shrink-0"
          >
            Tickets & More Info
          </a>
        ) : isTappable ? (
          <button
            data-testid={`${testIdPrefix}-view-venue-${ev.id}`}
            onClick={(e) => { e.stopPropagation(); onTap(); }}
            className="inline-flex items-center gap-1 border border-primary text-primary bg-transparent rounded-full px-3.5 py-2 text-[13px] font-bold hover:bg-primary/10 transition-colors shrink-0"
          >
            <MapPin className="w-3 h-3" />
            View Venue
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EventsListSection({
  events, loading, genre, userLat, userLng, onTap, testIdPrefix, emptyTitle, emptySubtitle,
}: {
  events: EventListItem[];
  loading: boolean;
  genre: EventGenre;
  userLat?: number;
  userLng?: number;
  onTap: (ev: EventListItem) => void;
  testIdPrefix: string;
  emptyTitle: string;
  emptySubtitle: string;
}) {
  const filtered = useMemo(() => {
    return events.filter(ev => matchesGenre(`${ev.title} ${ev.description ?? ""}`, genre));
  }, [events, genre]);

  const sorted = useMemo(() => {
    const hasLoc = userLat !== undefined && userLng !== undefined;
    const decorated = filtered.map((ev, idx) => {
      const d = hasLoc && ev.venueLat !== null && ev.venueLng !== null
        ? distanceKm(userLat!, userLng!, ev.venueLat, ev.venueLng)
        : null;
      return { ev, d, idx };
    });
    const withCoords = decorated.filter(x => x.d !== null).sort((a, b) => a.d! - b.d!);
    const withoutCoords = decorated.filter(x => x.d === null).sort((a, b) => a.idx - b.idx);
    return [...withCoords, ...withoutCoords];
  }, [filtered, userLat, userLng]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden animate-pulse">
            <div className="h-[130px] bg-zinc-800" />
            <div className="px-3.5 py-3 space-y-2">
              <div className="h-3 bg-zinc-800 rounded w-1/3" />
              <div className="h-3 bg-zinc-800 rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">🎉</div>
        <p className="text-zinc-200 font-bold text-base mb-1.5" data-testid={`${testIdPrefix}-empty`}>{emptyTitle}</p>
        <p className="text-zinc-500 text-[13px]">{emptySubtitle}</p>
      </div>
    );
  }

  return (
    <div>
      {sorted.map(({ ev, d }) => (
        <EventCardV4
          key={ev.id}
          ev={ev}
          dist={d}
          onTap={() => onTap(ev)}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}

function GenreChipRow({
  selected, onSelect, testIdPrefix,
}: {
  selected: EventGenre;
  onSelect: (g: EventGenre) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {EVENT_GENRES.map(g => (
        <button
          key={g}
          data-testid={`${testIdPrefix}-${g.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onSelect(g)}
          className={`flex-shrink-0 px-4 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
            selected === g
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700"
          }`}
        >
          {g}
        </button>
      ))}
    </div>
  );
}

function NearbyVenueRow({
  venue, onTap, dist, getCapacityBg, getCapacityColor, getTypeIcon,
}: {
  venue: Venue & { distance?: number | null };
  onTap: () => void;
  dist?: number | null;
  getCapacityBg: (level: number) => string;
  getCapacityColor: (level: number) => string;
  getTypeIcon: (type: string, vibrate?: boolean) => JSX.Element;
}) {
  const cap = (venue as any).capacityPercent ?? 0;
  const distance = dist ?? venue.distance ?? null;
  let nearestArea: string | null = null;
  let bestD = Infinity;
  for (const n of LONDON_NEIGHBOURHOODS) {
    const R = 6371;
    const dLat = (n.lat - venue.latitude) * Math.PI / 180;
    const dLon = (n.lng - venue.longitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(venue.latitude * Math.PI / 180) * Math.cos(n.lat * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (d < bestD) { bestD = d; nearestArea = n.name; }
  }
  const areaLabel = bestD <= 1.5 ? nearestArea : null;
  return (
    <div
      data-testid={`nearby-venue-${venue.id}`}
      onClick={onTap}
      className={`group relative isolate overflow-hidden flex items-center gap-3.5 px-4 py-3.5 rounded-2xl border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all cursor-pointer active:scale-[0.98] ${(venue as any).coverPhotoUrl ? "bg-zinc-900/80" : "bg-zinc-900"}`}
    >
      <CardPhotoBg url={(venue as any).coverPhotoUrl} />
      <div className={`w-[52px] h-[52px] rounded-full flex items-center justify-center border-2 shrink-0 ${getCapacityBg(cap)}`}>
        <span className={`text-sm font-bold ${getCapacityColor(cap)}`}>{cap}%</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-bold text-white truncate mb-0.5">{venue.name}</p>
        <div className="flex items-center gap-1 text-zinc-500 text-[12px]">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {distance != null
              ? (distance < 1 ? `${Math.round(distance * 1000)}m away` : `${distance.toFixed(1)}km away`)
              : (areaLabel ?? venue.type)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="border border-zinc-700 rounded-md px-2 py-0.5 text-[11px] text-zinc-300 capitalize">{venue.type}</span>
        <span className="text-zinc-600">{getTypeIcon(venue.type, venue.isSponsored)}</span>
      </div>
    </div>
  );
}

function MapBoundsTracker({ onBoundsChange, onZoomChange }: { onBoundsChange: (b: MapBounds) => void, onZoomChange: (z: number) => void }) {
  const map = useMap();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onZoomChangeRef = useRef(onZoomChange);
  onBoundsChangeRef.current = onBoundsChange;
  onZoomChangeRef.current = onZoomChange;

  useEffect(() => {
    const extractBounds = () => {
      const b = map.getBounds();
      onBoundsChangeRef.current({
        latMin: b.getSouth(),
        latMax: b.getNorth(),
        lngMin: b.getWest(),
        lngMax: b.getEast(),
      });
    };
    extractBounds();
    const handleChange = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(extractBounds, 400);
    };
    const handleZoom = () => {
      onZoomChangeRef.current(map.getZoom());
    };
    map.on("moveend", handleChange);
    map.on("zoomend", handleChange);
    map.on("zoom", handleZoom);
    return () => {
      map.off("moveend", handleChange);
      map.off("zoomend", handleChange);
      map.off("zoom", handleZoom);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [map]);
  return null;
}

// When the user closes the venue drawer back to the map, re-show that venue's
// name + capacity bubble (a standalone Leaflet popup) so they don't lose context.
// Works regardless of how the drawer was opened (map tap / Search / Explore /
// Events) by recentring on the venue and opening a popup styled like the marker
// bubbles. Tapping the bubble re-opens the drawer.
function RestoreVenueBubble({ venue, onOpen, onClear }: { venue: Venue | null; onOpen: (v: Venue) => void; onClear: () => void }) {
  const map = useMap();
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (!venue) return;
    if (typeof venue.latitude !== "number" || typeof venue.longitude !== "number") return;
    const latlng: [number, number] = [venue.latitude, venue.longitude];

    // Mirror the first-click map popup (ClusteredVenueMarkers): same class,
    // markup, colour dot, and live capacity from the enriched `capacityPercent`
    // field. `capacityLevel` is the stored column and is absent on map venue
    // objects, so it would render as "undefined".
    const colourCode = (venue as any).colourCode || "blue";
    const capColor = getCapacityColour(colourCode);
    const cap = (venue as any).capacityPercent ?? 0;
    const safeName = venue.name
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    const btn = document.createElement("button");
    btn.style.cssText = "background:none;border:none;padding:0;margin:0;cursor:pointer;text-align:left;";
    btn.setAttribute("data-testid", `popup-venue-${venue.id}`);
    btn.innerHTML = `<div data-venue-id="${venue.id}" style="cursor:pointer;padding:4px 0;min-width:120px;">
        <div style="font-weight:700;font-size:13px;color:#fafafa;font-family:'Outfit',sans-serif;">${safeName}</div>
        <div style="display:flex;align-items:center;gap:5px;margin-top:3px;">
          <span style="width:8px;height:8px;border-radius:50%;background:${capColor};display:inline-block;"></span>
          <span style="font-size:12px;color:#a1a1aa;">${cap}% capacity</span>
        </div>
      </div>`;

    const popup = L.popup({ className: "venue-preview-popup", closeButton: false, offset: [0, -4], autoPan: true })
      .setLatLng(latlng)
      .setContent(btn);

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      map.closePopup(popup);
      onOpenRef.current(venue);
    });

    // Recentre so the restored bubble is in view (needed when opened from
    // Search/Explore/Events where the map isn't on the venue). Keep the current
    // zoom if already close in, otherwise go to 16 so markers are declustered.
    const targetZoom = Math.max(map.getZoom(), 16);
    map.setView(latlng, targetZoom, { animate: true });

    // Open the popup once the view settles so autoPan positions it correctly.
    // Fall back to a timer in case no moveend fires (already centred).
    let opened = false;
    const openOnce = () => {
      if (opened) return;
      opened = true;
      popup.openOn(map);
    };
    map.once("moveend", openOnce);
    const openTimer = window.setTimeout(openOnce, 400);

    const handlePopupClose = (e: any) => {
      if (e.popup === popup) onClearRef.current();
    };
    map.on("popupclose", handlePopupClose);

    return () => {
      window.clearTimeout(openTimer);
      map.off("moveend", openOnce);
      map.off("popupclose", handlePopupClose);
      map.closePopup(popup);
    };
  }, [venue, map]);

  return null;
}

function SponsoredVenueMarkers({ venues, onVenueOpen, isBusinessUser, ownedVenueIds }: { venues: Venue[]; onVenueOpen: (v: Venue) => void; isBusinessUser?: boolean; ownedVenueIds?: Set<number> }) {
  const map = useMap();
  const onVenueOpenRef = useRef(onVenueOpen);
  onVenueOpenRef.current = onVenueOpen;

  useEffect(() => {
    const sponsoredMarkers: L.Marker[] = [];
    const sponsored = venues.filter((v) => {
      if (v.isSponsored !== true) return false;
      if (isBusinessUser) return ownedVenueIds?.has(v.id) ?? false;
      return true;
    });

    sponsored.forEach((venue) => {
      const latlng: [number, number] = [venue.latitude, venue.longitude];
      const isOwned = isBusinessUser && (ownedVenueIds?.has(venue.id) ?? false);
      const ownedShadow = isOwned
        ? "0 0 0 3px #f59e0b, 0 0 12px rgba(245,158,11,0.6), 0 2px 8px rgba(0,0,0,0.35)"
        : "0 2px 8px rgba(0,0,0,0.35)";
      const icon = L.divIcon({
        html: `<div style="
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: white;
          border: 2px solid #f59e0b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          line-height: 1;
          box-shadow: ${ownedShadow};
          cursor: pointer;
        ">🍯</div>`,
        className: "",
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
      const marker = L.marker(latlng, {
        icon,
        zIndexOffset: 1000,
      });
      const cap = (venue as any).capacityPercent ?? 0;
      const capColor = getCapacityColour((venue as any).colourCode || "blue");
      const safeName = venue.name.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
      const popupHtml = `<div data-venue-id="${venue.id}" style="cursor:pointer;padding:4px 0;min-width:120px;">
        <div style="font-weight:700;font-size:13px;color:#fafafa;font-family:'Outfit',sans-serif;">${safeName}</div>
        <div style="display:flex;align-items:center;gap:5px;margin-top:3px;">
          <span style="width:8px;height:8px;border-radius:50%;background:${capColor};display:inline-block;"></span>
          <span style="font-size:12px;color:#a1a1aa;">${cap}% capacity</span>
        </div>
      </div>`;
      marker.bindPopup(popupHtml, { className: "venue-preview-popup", closeButton: false, offset: [0, -4] });
      marker.on("popupopen", () => {
        const popup = marker.getPopup();
        const container = popup?.getElement();
        const handler = () => { map.closePopup(); onVenueOpenRef.current(venue); };
        container?.addEventListener("click", handler, { once: true });
      });
      marker.addTo(map);
      sponsoredMarkers.push(marker);
    });

    return () => {
      sponsoredMarkers.forEach((m) => map.removeLayer(m));
    };
  }, [venues, map, isBusinessUser, ownedVenueIds]);

  return null;
}

function ClusteredVenueMarkers({ venues, onVenueOpen, highlightOwned }: { venues: Venue[]; onVenueOpen: (v: Venue) => void; highlightOwned?: boolean }) {
  const map = useMap();
  const onVenueOpenRef = useRef(onVenueOpen);
  onVenueOpenRef.current = onVenueOpen;
  const mcgRef = useRef<any>(null);
  const markerMapRef = useRef<Map<number, L.Marker>>(new Map());

  // Create the cluster group once. Owned-highlight markers bypass clustering so they're
  // always visible at low zoom levels (the invisible cluster icon would otherwise hide them).
  useEffect(() => {
    if (highlightOwned) {
      mcgRef.current = null;
      return;
    }
    const mcg = (L as any).markerClusterGroup({
      chunkedLoading: true,
      animate: true,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 15,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      iconCreateFunction: () =>
        L.divIcon({ html: "", className: "", iconSize: [0, 0] }),
    });
    map.addLayer(mcg);
    mcgRef.current = mcg;
    return () => {
      map.removeLayer(mcg);
      mcgRef.current = null;
      markerMapRef.current.clear();
    };
  }, [map, highlightOwned]);

  // Diff venues and only add/remove changed markers
  useEffect(() => {
    const mcg = mcgRef.current;
    // For highlightOwned mode we add markers directly to the map, no cluster group required.
    if (!highlightOwned && !mcg) return;

    const incomingIds = new Set(venues.filter(v => highlightOwned || !v.isSponsored).map(v => v.id));
    const existingIds = new Set(markerMapRef.current.keys());

    // Remove markers no longer in venue list
    existingIds.forEach(id => {
      if (!incomingIds.has(id)) {
        const marker = markerMapRef.current.get(id);
        if (marker) {
          if (highlightOwned) map.removeLayer(marker);
          else mcg!.removeLayer(marker);
        }
        markerMapRef.current.delete(id);
      }
    });

    // Add only new markers
    venues.forEach((venue) => {
      if (!highlightOwned && venue.isSponsored === true) return;
      if (markerMapRef.current.has(venue.id)) return; // already exists

      const colourCode = (venue as any).colourCode || "blue";
      const fillColor = getCapacityColour(colourCode);
      const openState = (venue as any).isOpen;
      const isOpen = openState === true;
      const isUnknown = openState === null || openState === undefined;
      const latlng: [number, number] = [venue.latitude, venue.longitude];
      const cap = (venue as any).capacityPercent ?? 0;
      const opacity = isOpen ? "1" : isUnknown ? "0.65" : "0.5";
      const size = 36;
      const ownedRing = highlightOwned
        ? "box-shadow: 0 0 0 3px #f59e0b, 0 2px 10px rgba(0,0,0,0.4);"
        : "box-shadow: 0 2px 10px rgba(0,0,0,0.28);";
      const dotHtml = cap > 0
        ? `<div style="position:absolute;bottom:-2px;right:-2px;width:11px;height:11px;border-radius:6px;background:${fillColor};border:1.5px solid white;"></div>`
        : '';
      const busy = cap >= 85;
      const medium = cap >= 60 && cap < 85;
      const ringHtml = (isOpen && (busy || medium))
        ? `<div style="position:absolute;inset:-5px;border-radius:50%;border:2px solid ${fillColor}88;animation:${busy ? 'pulseRed 1.8s infinite' : 'pulseOrange 2.2s infinite'};pointer-events:none;"></div>`
        : '';
      const ringSize = (isOpen && (busy || medium)) ? size + 10 : size;
      const typeEmoji = venue.type === 'bar' ? '🍸'
        : venue.type === 'pub' ? '🍺'
        : venue.type === 'club' ? '🎉'
        : '🍸';
      const innerHtml = `<div style="
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          background: white;
          border: 2.5px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          line-height: 1;
          opacity: ${opacity};
          ${ownedRing}
          cursor: pointer;
          position: relative;
        ">${typeEmoji}${dotHtml}</div>`;
      const wrapperHtml = `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">${ringHtml}${innerHtml}</div>`;
      const icon = L.divIcon({
        html: wrapperHtml,
        className: "",
        iconSize: [ringSize, ringSize],
        iconAnchor: [ringSize / 2, ringSize / 2],
      });
      const pinMarker = L.marker(latlng, { icon, zIndexOffset: highlightOwned ? 500 : 0 });
      const capColor = getCapacityColour(colourCode);
      const safeName = venue.name.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
      const popupHtml = `<div data-venue-id="${venue.id}" style="cursor:pointer;padding:4px 0;min-width:120px;">
        <div style="font-weight:700;font-size:13px;color:#fafafa;font-family:'Outfit',sans-serif;">${safeName}</div>
        <div style="display:flex;align-items:center;gap:5px;margin-top:3px;">
          <span style="width:8px;height:8px;border-radius:50%;background:${capColor};display:inline-block;"></span>
          <span style="font-size:12px;color:#a1a1aa;">${cap}% capacity</span>
        </div>
      </div>`;
      pinMarker.bindPopup(popupHtml, { className: "venue-preview-popup", closeButton: false, offset: [0, -4] });
      pinMarker.on("popupopen", () => {
        const popup = pinMarker.getPopup();
        const container = popup?.getElement();
        const handler = () => { map.closePopup(); onVenueOpenRef.current(venue); };
        container?.addEventListener("click", handler, { once: true });
      });
      if (highlightOwned) pinMarker.addTo(map);
      else mcg!.addLayer(pinMarker);
      markerMapRef.current.set(venue.id, pinMarker);
    });

    if (highlightOwned) {
      return () => {
        markerMapRef.current.forEach(marker => map.removeLayer(marker));
        markerMapRef.current.clear();
      };
    }
  }, [venues, highlightOwned, map]);

  return null;
}

function FriendPins({ venues, onVenueOpen }: { venues?: Venue[]; onVenueOpen: (v: Venue) => void }) {
  const map = useMap();
  const { data: pins } = useFriendMapPins();
  const onVenueOpenRef = useRef(onVenueOpen);
  onVenueOpenRef.current = onVenueOpen;
  const venuesRef = useRef(venues);
  venuesRef.current = venues;

  useEffect(() => {
    if (!pins || !Array.isArray(pins) || pins.length === 0) return;
    const markers: L.Marker[] = [];

    pins.forEach((pin: any) => {
      if (!pin.latitude || !pin.longitude) return;
      const size = 30;
      const initial = (pin.username?.[0]?.toUpperCase() || "?")
        .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
      const safeName = (pin.username || "")
        .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
      const onlineDot = `<div style="position:absolute;bottom:-1px;right:-1px;width:9px;height:9px;border-radius:5px;background:#22c55e;border:2px solid white;"></div>`;
      const avatar = `<div style="
            position: relative;
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3B6AE8, #6A4DE8);
            border: 2.5px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 800;
            font-size: 12px;
            line-height: 1;
            box-shadow: 0 3px 10px rgba(0,0,0,0.35);
          ">${initial}${onlineDot}</div>`;
      const tail = `<div style="width:0;height:0;margin:0 auto;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid white;"></div>`;
      const tag = `<div style="background:white;border-radius:7px;padding:2px 7px;font-size:10px;font-weight:700;color:#111;text-align:center;white-space:nowrap;margin-top:2px;box-shadow:0 2px 6px rgba(0,0,0,0.2);font-family:'Inter',sans-serif;">@${safeName}</div>`;
      const markerHtml = `<div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;">${avatar}${tail}${tag}</div>`;

      const totalH = size + 28;
      const marker = L.marker([pin.latitude, pin.longitude], {
        icon: L.divIcon({
          html: markerHtml,
          className: "",
          iconSize: [80, totalH],
          iconAnchor: [40, totalH],
        }),
        zIndexOffset: 2000,
      });

      marker.bindTooltip(`@${pin.username} — ${pin.venueName}`, {
        direction: "top",
        offset: [0, -10],
        className: "friend-pin-tooltip",
      });

      if (pin.lastVenueId != null) {
        marker.on("click", () => {
          const found = venuesRef.current?.find(v => v.id === pin.lastVenueId);
          const venue = found ?? ({
            id: pin.lastVenueId,
            name: pin.venueName,
            latitude: pin.latitude,
            longitude: pin.longitude,
          } as any);
          onVenueOpenRef.current(venue);
        });
      }

      marker.addTo(map);
      markers.push(marker);
    });

    return () => {
      markers.forEach(m => map.removeLayer(m));
    };
  }, [pins, map]);

  return null;
}

const VENUE_TYPES = ["Bar", "Pub", "Club"];
const VIBE_TYPES = ["Quiet", "Moderate", "Busy"];

const LONDON_CENTER: [number, number] = [51.505, -0.09];

export default function Home() {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(LONDON_CENTER);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [restoreBubbleVenue, setRestoreBubbleVenue] = useState<Venue | null>(null);
  // When the venue drawer is opened from the Events page, remember to return
  // there on close (instead of dropping the user on the map).
  const [venueReturnToEvents, setVenueReturnToEvents] = useState(false);
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [vibeFilters, setVibeFilters] = useState<Set<string>>(new Set());
  const [showClosed, setShowClosed] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [currentZoom, setCurrentZoom] = useState(11);
  const [recenterVersion, setRecenterVersion] = useState(0);
  const [isRecentering, setIsRecentering] = useState(false);
  const [geolocating, setGeolocating] = useState(true);
  const [overlayFading, setOverlayFading] = useState(false);
  const geoResolvedRef = useRef(false);
  // Device compass: an always-on direction cone on the user dot. The map can be
  // freely rotated by touch; the cone compensates for the live map bearing so it
  // always points at the real-world direction the device is facing.
  const { heading, status: headingStatus, enable: enableHeading } = useDeviceHeading();
  const [mapBearing, setMapBearing] = useState(0);
  const [resetBearingVersion, setResetBearingVersion] = useState(0);
  const [compassHintDismissed, setCompassHintDismissed] = useState(false);
  const showHeadingCone = heading !== null && userLocation !== null;
  const coneRotation = (heading ?? 0) + mapBearing;
  const handleBearingChange = useCallback((deg: number) => setMapBearing(deg), []);
  // iOS gates the compass behind a permission prompt that only fires from a user
  // gesture. Show a small tappable hint when orientation needs granting and no
  // heading has arrived yet; it self-hides once a heading flows in.
  const needsCompassPermission =
    typeof DeviceOrientationEvent !== "undefined" &&
    typeof (DeviceOrientationEvent as any).requestPermission === "function";
  const showCompassHint =
    needsCompassPermission &&
    heading === null &&
    headingStatus !== "active" &&
    !compassHintDismissed;

  // Request orientation access at startup alongside location. On iOS,
  // DeviceOrientationEvent.requestPermission must run inside a user gesture, so we
  // arm it on the first tap; elsewhere orientation needs no permission and we
  // subscribe immediately.
  useEffect(() => {
    const hasPermissionApi =
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof (DeviceOrientationEvent as any).requestPermission === "function";
    if (!hasPermissionApi) {
      enableHeading();
      return;
    }
    let done = false;
    const onGesture = () => {
      if (done) return;
      done = true;
      enableHeading();
      window.removeEventListener("click", onGesture);
      window.removeEventListener("touchend", onGesture);
    };
    // iOS only honours requestPermission from a "click"/"touchend" gesture;
    // pointerdown/touchstart can silently fail to qualify on some versions.
    window.addEventListener("click", onGesture);
    window.addEventListener("touchend", onGesture);
    return () => {
      window.removeEventListener("click", onGesture);
      window.removeEventListener("touchend", onGesture);
    };
  }, [enableHeading]);
  // v4 nav model: full-screen page swap for Map/Nearby/Events/Search; Profile is an overlay sheet.
  // Search lives in the same `sheet` slot as Profile so only one is ever open at a time,
  // but it renders as a full-screen page (z-[500]) so the bottom nav stays clickable on top.
  const [nav, setNav] = useState<'map' | 'nearby' | 'events'>('map');
  const [sheet, setSheet] = useState<'profile' | 'search' | 'notifications' | 'settings' | null>(null);
  const [profileDefaultTab, setProfileDefaultTab] = useState<string | undefined>(undefined);
  const [nearbySearch, setNearbySearch] = useState("");
  const searchOpen = sheet === 'search';
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();
  const isBusinessUser = user?.role === "business";
  const { data: myVenuesData } = useQuery<Venue[]>({
    queryKey: ["/api/business/my-venues"],
    enabled: isBusinessUser,
  });
  const ownedVenueIds = useMemo(
    () => new Set((myVenuesData ?? []).map(v => v.id)),
    [myVenuesData],
  );

  type FeedMediaItem = {
    id: number; venueId: number; venueName: string;
    userId: number; username: string;
    mediaType: "photo" | "video";
    cloudinaryUrl: string; thumbnailUrl: string | null;
    durationSeconds: number | null;
    thumbsUp: number; thumbsDown: number;
    createdAt: string;
  };
  type UserSearchResult = { id: number; username: string };
  type UserMediaItem = {
    id: number; venueId: number; venueName: string;
    mediaType: "photo" | "video";
    cloudinaryUrl: string; thumbnailUrl: string | null;
    durationSeconds: number | null;
    thumbsUp: number; thumbsDown: number;
    createdAt: string;
  };

  const [eventFilter, setEventFilter] = useState<"tonight" | "this_week">("tonight");
  const [eventGenre, setEventGenre] = useState<EventGenre>("All");
  const [nearbyTab, setNearbyTab] = useState<"discover" | "vibes" | "events">("discover");

  type UserVibeItem = {
    id: number;
    venueId: number;
    venueName: string;
    type: "media";
    date: string;
    capacityLevel?: number | null;
    vibe?: string | null;
    mediaType?: "photo" | "video";
    cloudinaryUrl?: string;
    thumbnailUrl?: string | null;
    userId: number;
    username: string;
    profilePhotoUrl?: string | null;
  };

  type EventItem = {
    id: number;
    venueId: number | null;
    title: string;
    description: string | null;
    startTime: string;
    endTime: string | null;
    imageUrl: string | null;
    ticketUrl: string | null;
    source: "business" | "resident_advisor";
    externalId: string | null;
    raVenueName: string | null;
    raVenueAddress: string | null;
    venueName: string | null;
    venueLat: number | null;
    venueLng: number | null;
  };
  const [selectedFeedMedia, setSelectedFeedMedia] = useState<FeedMediaItem | null>(null);
  const [vibeReturnToSearch, setVibeReturnToSearch] = useState(false);
  const [viewerReactions, setViewerReactions] = useState<Record<number, "thumbs_up" | "thumbs_down" | null>>({});
  const [viewerCounts, setViewerCounts] = useState<Record<number, { thumbsUp: number; thumbsDown: number }>>({});
  const [atUsername, setAtUsername] = useState("");
  const [viewingUsername, setViewingUsername] = useState<string | null>(null);
  const atDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logout = useLogout();
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  
  const dismissOverlay = useCallback(() => {
    setOverlayFading(true);
    setTimeout(() => {
      setGeolocating(false);
      setIsRecentering(false);
      setOverlayFading(false);
    }, 500);
  }, []);

  const handleRecenterComplete = useCallback(() => {
    if (geoResolvedRef.current) return;
    geoResolvedRef.current = true;
    dismissOverlay();
  }, [dismissOverlay]);

  const requestRecenter = useCallback((center: [number, number], zoom: number) => {
    setMapCenter(center);
    setCurrentZoom(zoom);
    setRecenterVersion(v => v + 1);
  }, []);

  const { data: venues, isLoading } = useVenues(
    mapBounds,
    showClosed,
    searchQuery,
    userLocation ? { lat: userLocation[0], lng: userLocation[1] } : null,
  );
  const { data: sponsoredVenuesData } = useSponsoredVenues();
  const { data: heatVenues } = useHeatVenues();

  const { data: favourites } = useQuery<Favourite[]>({
    queryKey: ['/api/favourites'],
    enabled: !!user,
  });

  useEffect(() => {
    let dismissed = false;
    const fallback = setTimeout(() => {
      if (!dismissed) {
        dismissed = true;
        geoResolvedRef.current = true;
        dismissOverlay();
      }
    }, 5000);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (dismissed) return;
          clearTimeout(fallback);
          const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(coords);
          const lat = pos.coords.latitude;
          if (lat >= 51.2 && lat <= 51.8) {
            requestRecenter(coords, 16);
          } else {
            dismissed = true;
            geoResolvedRef.current = true;
            dismissOverlay();
          }
        },
        () => {
          if (dismissed) return;
          dismissed = true;
          clearTimeout(fallback);
          geoResolvedRef.current = true;
          dismissOverlay();
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      dismissed = true;
      clearTimeout(fallback);
      geoResolvedRef.current = true;
      dismissOverlay();
    }
    return () => clearTimeout(fallback);
  }, []);

  const toggleTypeFilter = (type: string) => {
    setTypeFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const toggleVibeFilter = (vibe: string) => {
    setVibeFilters(prev => {
      const next = new Set(prev);
      if (next.has(vibe)) next.delete(vibe); else next.add(vibe);
      return next;
    });
  };

  const filteredVenues = useMemo(() => {
    return venues?.filter(v => {
      const matchesType = typeFilters.size === 0 || typeFilters.has(v.type.charAt(0).toUpperCase() + v.type.slice(1).toLowerCase());
      const cap = (v as any).capacityPercent ?? 0;

      let matchesVibe = true;
      if (vibeFilters.size > 0) {
        matchesVibe = false;
        if (vibeFilters.has("Quiet") && cap < 30) matchesVibe = true;
        if (vibeFilters.has("Moderate") && cap >= 30 && cap < 70) matchesVibe = true;
        if (vibeFilters.has("Busy") && cap >= 70) matchesVibe = true;
      }

      return matchesType && matchesVibe;
    }) || [];
  }, [venues, typeFilters, vibeFilters]);

  // Heat dataset for the map heatmap. Sourced from the city-wide, viewport-independent
  // /api/venues/heat set so panning/zooming never changes the heat's input data (it bakes
  // once and stays glued, refreshing only on the 30-minute timer). Type/vibe filters are
  // applied client-side so filter toggles still re-bake the heat, but viewport changes do not.
  const heatFilteredVenues = useMemo(() => {
    return (heatVenues || []).filter(v => {
      const matchesType = typeFilters.size === 0 || typeFilters.has(v.type.charAt(0).toUpperCase() + v.type.slice(1).toLowerCase());
      const cap = (v as any).capacityPercent ?? 0;

      let matchesVibe = true;
      if (vibeFilters.size > 0) {
        matchesVibe = false;
        if (vibeFilters.has("Quiet") && cap < 30) matchesVibe = true;
        if (vibeFilters.has("Moderate") && cap >= 30 && cap < 70) matchesVibe = true;
        if (vibeFilters.has("Busy") && cap >= 70) matchesVibe = true;
      }

      return matchesType && matchesVibe;
    });
  }, [heatVenues, typeFilters, vibeFilters]);

  // Owned-venue marker dataset for business users. Sourced from the full /api/business/my-venues
  // response (not viewport-scoped) so every owned venue stays visible at low zoom levels, where
  // /api/venues' bounds-scoped LIMIT 500 may exclude some of them. Live capacity / colour / open
  // state are merged in from the raw viewport response (venues), NOT filteredVenues, so live
  // values still apply when the user has a type/vibe filter active that would exclude the owned
  // venue from the standard layer. Owned markers themselves intentionally ignore those filters.
  const ownedVenueMarkers = useMemo(() => {
    if (!isBusinessUser || !myVenuesData?.length) return [];
    const liveById = new Map((venues ?? []).map(v => [v.id, v]));
    return myVenuesData.map(v => liveById.get(v.id) ?? v);
  }, [isBusinessUser, myVenuesData, venues]);

  // Sponsored marker dataset — sourced from /api/venues/sponsored (no viewport limit)
  // so Honey markers stay visible at any zoom level even when /api/venues' bounds-scoped
  // LIMIT 500 would exclude them. Live capacity / colour / open state are merged in from
  // the raw viewport response (venues) when available, mirroring the ownedVenueMarkers
  // pattern. Type/vibe filters intentionally do NOT apply to sponsored markers.
  const sponsoredVenueMarkers = useMemo(() => {
    if (!sponsoredVenuesData?.length) return [] as Venue[];
    const liveById = new Map((venues ?? []).map(v => [v.id, v]));
    return sponsoredVenuesData.map(v => (liveById.get(v.id) ?? v) as Venue);
  }, [sponsoredVenuesData, venues]);

  // Merge viewport venues with the full sponsored list so paid venues outside the
  // current map bounds (or excluded by /api/venues' LIMIT 500) still surface in
  // search and neighbourhood results. Live capacity / colour / open state come
  // from the viewport response when available, falling back to the sponsored
  // record otherwise. Mirrors the sponsoredVenueMarkers pattern.
  const searchableVenues = useMemo(() => {
    const liveById = new Map((venues ?? []).map(v => [v.id, v]));
    const merged: Venue[] = [...(venues ?? [])];
    (sponsoredVenuesData ?? []).forEach(s => {
      if (!liveById.has(s.id)) merged.push(s as Venue);
    });
    return merged;
  }, [venues, sponsoredVenuesData]);

  const searchVenues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const all = searchableVenues;
    if (!q) {
      return [...all].sort((a, b) => {
        if (b.isSponsored !== a.isSponsored) return b.isSponsored ? 1 : -1;
        return ((b as any).capacityPercent ?? 0) - ((a as any).capacityPercent ?? 0);
      });
    }
    // For real searches (q ≥ 2 chars) the server has already filtered + sorted
    // by sponsored + proximity. We then apply a prefix-match boost on top so
    // names/types that START with the query rank above those that merely
    // contain it, while preserving the sponsored-first ordering.
    const source = q.length >= 2 ? (venues ?? []) : all;
    const scored = source
      .map(v => {
        const name = v.name.toLowerCase();
        const type = v.type.toLowerCase();
        if (!name.includes(q) && !type.includes(q)) return null;
        const score = (name.startsWith(q) || type.startsWith(q)) ? 0 : 1;
        return { venue: v, score };
      })
      .filter((x): x is { venue: typeof source[0]; score: number } => x !== null)
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        if (b.venue.isSponsored !== a.venue.isSponsored) return b.venue.isSponsored ? 1 : -1;
        return ((b.venue as any).capacityPercent ?? 0) - ((a.venue as any).capacityPercent ?? 0);
      });
    return scored.map(x => x.venue);
  }, [searchableVenues, searchQuery, venues]);

  const searchNeighbourhoods = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || searchQuery.startsWith("@")) return [];
    return matchNeighbourhoods(q);
  }, [searchQuery]);

  const neighbourhoodVenues = useMemo(() => {
    if (searchNeighbourhoods.length === 0) return searchVenues;
    const q = searchQuery.trim().toLowerCase();
    const all = searchableVenues;
    return all.filter(v => {
      const name = v.name.toLowerCase();
      const type = v.type.toLowerCase();
      if (!name.includes(q) && !type.includes(q)) return false;
      return searchNeighbourhoods.some(n =>
        haversineDistance(v.latitude, v.longitude, n.lat, n.lng) <= 1
      );
    });
  }, [searchVenues, searchNeighbourhoods, searchableVenues, searchQuery]);

  const activeFiltersCount = typeFilters.size + vibeFilters.size + (showClosed ? 1 : 0);

  // Debounce @username search
  useEffect(() => {
    if (!searchQuery.startsWith("@")) { setAtUsername(""); return; }
    const val = searchQuery.slice(1).trim();
    if (atDebounceRef.current) clearTimeout(atDebounceRef.current);
    atDebounceRef.current = setTimeout(() => setAtUsername(val), 300);
    return () => { if (atDebounceRef.current) clearTimeout(atDebounceRef.current); };
  }, [searchQuery]);

  // Reset @-mode state when search closes
  useEffect(() => {
    if (!searchOpen) { setViewingUsername(null); setAtUsername(""); }
  }, [searchOpen]);

  const { data: userSearchData, isLoading: userSearchLoading } = useQuery<{ users: UserSearchResult[] }>({
    queryKey: ["/api/users/search", atUsername],
    queryFn: () => fetch(`/api/users/search?username=${encodeURIComponent(atUsername)}`, { credentials: "include" }).then(r => r.json()),
    enabled: searchOpen && searchQuery.startsWith("@") && atUsername.length > 0,
    staleTime: 60000,
  });

  const { data: userMediaData, isLoading: userMediaLoading } = useQuery<{ user: { id: number; username: string }; media: UserMediaItem[] }>({
    queryKey: ["/api/users", viewingUsername, "media"],
    queryFn: () => fetch(`/api/users/${viewingUsername}/media`, { credentials: "include" }).then(r => r.json()),
    enabled: !!viewingUsername,
    staleTime: 60000,
  });

  const userLat = userLocation?.[0];
  const userLng = userLocation?.[1];
  const { data: eventsData, isLoading: eventsLoading } = useQuery<EventItem[]>({
    queryKey: ["/api/events", eventFilter, userLat, userLng],
    queryFn: async () => {
      const params = new URLSearchParams({ filter: eventFilter });
      if (userLat !== undefined && userLng !== undefined) {
        params.set("lat", String(userLat));
        params.set("lng", String(userLng));
      }
      const res = await fetch(`/api/events?${params.toString()}`, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Failed to load events: ${res.status}`);
      }
      const json = await res.json();
      return Array.isArray(json) ? (json as EventItem[]) : [];
    },
    enabled: nav === 'events' || (nav === 'nearby' && nearbyTab === "events"),
    staleTime: 5 * 60 * 1000,
  });

  const { data: userVibesData, isLoading: userVibesLoading } = useQuery<{ vibes: UserVibeItem[] }>({
    queryKey: ["/api/vibes/explore"],
    enabled: !!user && nav === 'nearby' && nearbyTab === "vibes",
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  function formatEventDateTime(iso: string): string {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-GB", { weekday: "short" });
    const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    let time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true });
    time = time.toLowerCase().replace(/\s+/g, "");
    return `${day} ${date} · ${time}`;
  }

  function eventDistanceKm(ev: EventItem): number | null {
    if (userLat === undefined || userLng === undefined || ev.venueLat === null || ev.venueLng === null) return null;
    return haversineDistance(userLat, userLng, ev.venueLat, ev.venueLng);
  }

  async function handleEventTap(ev: EventItem) {
    if (ev.venueId && ev.venueLat !== null && ev.venueLng !== null) {
      requestRecenter([ev.venueLat, ev.venueLng], 16);
      try {
        const res = await fetch(`/api/venues/${ev.venueId}`, { credentials: "include" });
        if (res.ok) {
          const venue = await res.json();
          if (nav === 'events') {
            // Opened from the full Events page: keep it as the underlying page
            // so closing the drawer returns the user to Events (not the map).
            setSheet(null);
            setSearchQuery("");
            setRestoreBubbleVenue(null);
            setVenueReturnToEvents(true);
            setSelectedVenue(venue);
          } else {
            // Any other origin (e.g. Nearby → Events tab): preserve existing
            // behaviour — fly to the venue on the map and open the drawer there.
            resetToMap(() => setSelectedVenue(venue));
          }
        }
      } catch {
        // ignore — at least the map flew to location
      }
    } else if (ev.ticketUrl) {
      openExternal(ev.ticketUrl);
    }
  }

  function openFeedMedia(item: FeedMediaItem) {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.log("[vibe viewer] open feed media:", {
        id: item.id, mediaType: item.mediaType,
        cloudinaryUrl: item.cloudinaryUrl, thumbnailUrl: item.thumbnailUrl,
      });
    }
    setVibeReturnToSearch(true);
    setSelectedFeedMedia(item);
    setViewerCounts(c => ({ ...c, [item.id]: { thumbsUp: item.thumbsUp, thumbsDown: item.thumbsDown } }));
  }

  function openUserMediaItem(item: UserMediaItem) {
    const asFeed: FeedMediaItem = { ...item, userId: 0, username: viewingUsername ?? "", venueName: item.venueName };
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.log("[vibe viewer] open user media:", {
        id: item.id, mediaType: item.mediaType,
        cloudinaryUrl: item.cloudinaryUrl, thumbnailUrl: item.thumbnailUrl,
      });
    }
    setVibeReturnToSearch(true);
    setSelectedFeedMedia(asFeed);
    setViewerCounts(c => ({ ...c, [item.id]: { thumbsUp: item.thumbsUp, thumbsDown: item.thumbsDown } }));
  }

  function closeFeedMedia() {
    setSelectedFeedMedia(null);
    if (vibeReturnToSearch) {
      openSheet('search');
      setVibeReturnToSearch(false);
    }
  }

  function openUserVibes(targetUserId: number, source: "search" | "venueDetail") {
    if (user && targetUserId === (user as any).id) {
      setSelectedFeedMedia(null);
      setVibeReturnToSearch(false);
      openSheet('profile', { profileTab: "vibes" });
      return;
    }
    try {
      sessionStorage.setItem("vibeReturnTo", source);
      if (source === "venueDetail" && selectedVenue) {
        sessionStorage.setItem("vibeReturnVenueId", String(selectedVenue.id));
      } else {
        sessionStorage.removeItem("vibeReturnVenueId");
      }
    } catch {}
    setSelectedFeedMedia(null);
    setLocation(`/friends/${targetUserId}/vibes`);
  }

  useEffect(() => {
    if (location !== "/") return;
    let returnTo: string | null = null;
    let venueIdStr: string | null = null;
    try {
      returnTo = sessionStorage.getItem("vibeReturnTo");
      venueIdStr = sessionStorage.getItem("vibeReturnVenueId");
    } catch {}
    if (!returnTo) return;
    try {
      sessionStorage.removeItem("vibeReturnTo");
      sessionStorage.removeItem("vibeReturnVenueId");
    } catch {}
    if (returnTo === "search") {
      openSheet('search');
    } else if (returnTo === "venueDetail" && venueIdStr) {
      const vid = Number(venueIdStr);
      const found = venues?.find(v => v.id === vid);
      openVenueSheet(found ?? ({ id: vid } as any));
    }
  }, [location, venues]);

  // Push-notification deep links. The service worker forwards the notification's
  // `url` (e.g. `/?venue=12`, `/?profile=friends`, `/?media=5`) either by
  // navigating a freshly-launched window or by posting NOTIFICATION_CLICK to an
  // already-open client (App.tsx routes that to setLocation). Either way the
  // query params land here — parse them, open the matching overlay, then clear
  // the params so a refetch / back-nav doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const venueParam = params.get("venue");
    const profileParam = params.get("profile");
    const mediaParam = params.get("media");
    const friendParam = params.get("friend");
    if (!venueParam && !profileParam && !mediaParam && !friendParam) return;

    setLocation("/", { replace: true });

    if (profileParam) {
      openSheet('profile', { profileTab: profileParam });
      return;
    }
    if (friendParam) {
      // Fly the map to the friend's live marker if they're sharing location.
      // Otherwise fall back to the carried venue (where they reported), and
      // failing that, their vibe history. We fetch map-pins fresh here rather
      // than reading react-query state so a cold load (data not yet ready)
      // doesn't skip straight to the vibe-history fallback.
      const fid = Number(friendParam);
      if (!Number.isFinite(fid)) return;
      const carriedVenue = venueParam && Number.isFinite(Number(venueParam)) ? Number(venueParam) : null;
      const fallbackToVenueOrVibes = () => {
        if (carriedVenue != null) {
          const found = venues?.find(v => v.id === carriedVenue);
          if (found) { openVenueSheet(found); return; }
          fetch(`/api/venues/${carriedVenue}`, { credentials: "include" })
            .then(r => (r.ok ? r.json() : null))
            .then(v => { if (v) openVenueSheet(v); else setLocation(`/friends/${fid}/vibes`); })
            .catch(() => setLocation(`/friends/${fid}/vibes`));
          return;
        }
        setLocation(`/friends/${fid}/vibes`);
      };
      fetch(`/api/friends/map-pins`, { credentials: "include" })
        .then(r => (r.ok ? r.json() : []))
        .then((pins: any[]) => {
          const pin = Array.isArray(pins) ? pins.find(p => p.id === fid) : undefined;
          if (pin && pin.latitude != null && pin.longitude != null) {
            requestRecenter([pin.latitude, pin.longitude], 16);
            resetToMap();
            return;
          }
          fallbackToVenueOrVibes();
        })
        .catch(() => fallbackToVenueOrVibes());
      return;
    }
    if (venueParam) {
      const vid = Number(venueParam);
      if (!Number.isFinite(vid)) return;
      const found = venues?.find(v => v.id === vid);
      if (found) {
        openVenueSheet(found);
        return;
      }
      fetch(`/api/venues/${vid}`, { credentials: "include" })
        .then(r => (r.ok ? r.json() : null))
        .then(v => openVenueSheet(v ?? ({ id: vid } as any)))
        .catch(() => openVenueSheet({ id: vid } as any));
      return;
    }
    if (mediaParam) {
      const mid = Number(mediaParam);
      if (!Number.isFinite(mid)) return;
      fetch(`/api/media/feed`, { credentials: "include" })
        .then(r => (r.ok ? r.json() : null))
        .then((data: any) => {
          // /api/media/feed returns { feed: [...] }, not a bare array.
          const items: FeedMediaItem[] = Array.isArray(data) ? data : (data?.feed ?? []);
          const item = items.find(i => i.id === mid);
          if (item) openFeedMedia(item);
        })
        .catch(() => {});
    }
  }, [searchString]);

  async function handleViewerReact(mediaId: number, reaction: "thumbs_up" | "thumbs_down") {
    if (!user) return;
    const prev = viewerReactions[mediaId] ?? null;
    const prevCounts = viewerCounts[mediaId] ?? { thumbsUp: 0, thumbsDown: 0 };
    const newCounts = { ...prevCounts };
    let newReaction: "thumbs_up" | "thumbs_down" | null = reaction;
    if (prev === reaction) {
      newReaction = null;
      newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
    } else {
      if (prev) newCounts[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, newCounts[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
      newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] += 1;
    }
    setViewerReactions(r => ({ ...r, [mediaId]: newReaction }));
    setViewerCounts(c => ({ ...c, [mediaId]: newCounts }));
    try {
      await fetch(`/api/media/${mediaId}/react`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ reaction }),
      });
    } catch {
      setViewerReactions(r => ({ ...r, [mediaId]: prev }));
      setViewerCounts(c => ({ ...c, [mediaId]: prevCounts }));
    }
  }

  function handleViewVenue(venueId: number) {
    setSelectedFeedMedia(null);
    setVibeReturnToSearch(false);
    setSearchQuery("");
    setViewingUsername(null);
    const found = venues?.find(v => v.id === venueId);
    if (found) {
      requestRecenter([found.latitude, found.longitude], 16);
      resetToMap(() => setSelectedVenue(found));
    }
  }

  const nearbyVenues = useMemo(() => {
    const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const filtered = (venues || []).filter(v =>
      (v.name.toLowerCase().includes(nearbySearch.toLowerCase()) ||
      v.type.toLowerCase().includes(nearbySearch.toLowerCase()))
    );

    if (!userLocation) {
      return filtered.map(v => ({ ...v, distance: null as number | null }));
    }

    return filtered
      .map(v => ({
        ...v,
        distance: haversineDistance(userLocation[0], userLocation[1], v.latitude, v.longitude) as number | null
      }))
      .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
  }, [venues, nearbySearch, userLocation]);

  const getCapacityColor = (level: number) => {
    if (level >= 70) return "text-red-400";
    if (level >= 31) return "text-orange-400";
    return "text-blue-400";
  };

  const getCapacityBg = (level: number) => {
    if (level >= 70) return "bg-red-500/20 border-red-500/20";
    if (level >= 31) return "bg-orange-500/20 border-orange-500/20";
    return "bg-blue-500/20 border-blue-500/20";
  };

  const getCapacityLabel = sharedGetCapacityLabel;
  const getCapacityLabelColor = getCapacityLabelTextClass;

  const venueArea = useCallback((v: Venue): string | null => {
    let nearest: { name: string; d: number } | null = null;
    for (const n of LONDON_NEIGHBOURHOODS) {
      const d = haversineDistance(v.latitude, v.longitude, n.lat, n.lng);
      if (!nearest || d < nearest.d) nearest = { name: n.name, d };
    }
    return nearest && nearest.d <= 1.5 ? nearest.name : null;
  }, []);

  const hotVenues = useMemo(() => {
    return (venues ?? [])
      .filter(v => ((v as any).capacityPercent ?? 0) >= 70)
      .sort((a, b) => (((b as any).capacityPercent ?? 0) - ((a as any).capacityPercent ?? 0)))
      .slice(0, 8);
  }, [venues]);

  const quietVenues = useMemo(() => {
    return (venues ?? [])
      .filter(v => {
        const c = (v as any).capacityPercent ?? 0;
        return c > 0 && c < 30;
      })
      .sort((a, b) => (((a as any).capacityPercent ?? 0) - ((b as any).capacityPercent ?? 0)))
      .slice(0, 5);
  }, [venues]);

  const browseAreas = useMemo(() => {
    const buckets = new Map<string, { name: string; lat: number; lng: number; venues: Venue[] }>();
    for (const v of (venues ?? [])) {
      const area = venueArea(v);
      if (!area) continue;
      const found = LONDON_NEIGHBOURHOODS.find(n => n.name === area);
      if (!found) continue;
      const cur = buckets.get(area) ?? { name: area, lat: found.lat, lng: found.lng, venues: [] };
      cur.venues.push(v);
      buckets.set(area, cur);
    }
    return Array.from(buckets.values())
      .sort((a, b) => b.venues.length - a.venues.length)
      .slice(0, 6);
  }, [venues, venueArea]);

  const nearbySearchVenues = useMemo(() => {
    const q = nearbySearch.trim().toLowerCase();
    if (!q) return [] as Venue[];
    return (venues ?? []).filter(v =>
      v.name.toLowerCase().includes(q) || v.type.toLowerCase().includes(q)
    );
  }, [venues, nearbySearch]);

  const nearbySearchNeighbourhoods = useMemo(() => {
    const q = nearbySearch.trim().toLowerCase();
    if (!q) return [] as LondonNeighbourhood[];
    return matchNeighbourhoods(q);
  }, [nearbySearch]);

  const getTypeIcon = (type: string, vibrate = false) => {
    const cls = `w-4 h-4${vibrate ? ' sponsored-vibrate' : ''}`;
    switch (type.toLowerCase()) {
      case 'bar':  return <GlassWater className={cls} />;
      case 'pub':  return <GlassWater className={cls} />;
      case 'club': return <Music className={cls} />;
      default:     return <GlassWater className={cls} />;
    }
  };

  // Single-sheet exclusivity: only one of Profile / Search / Venue can be open at a time.
  // Opening any sheet closes the others. This avoids stranded overlay state.
  const openSheet = (target: 'profile' | 'search', opts?: { profileTab?: string }) => {
    if (target === 'profile') {
      // Re-tapping the active Profile button toggles back to the Map (closes the
      // sheet). If a caller explicitly requests a tab (e.g. notification banner
      // → friends), always honor it instead of toggling.
      const tabRequested = opts && 'profileTab' in opts;
      if (sheet === 'profile' && !tabRequested) {
        resetToMap();
        return;
      }
      setSelectedVenue(null);
      setRestoreBubbleVenue(null);
      setVenueReturnToEvents(false);
      if (tabRequested) {
        // Explicit tab request (e.g. notification banner → friends) wins.
        setProfileDefaultTab(opts!.profileTab);
      } else if (sheet !== 'profile') {
        // Opening from a closed state via the Account button — reset to the
        // default tab so a previous notification deep-link doesn't persist.
        setProfileDefaultTab(undefined);
      }
      setSearchQuery("");
      setSheet('profile');
    } else {
      if (sheet === 'search' && !selectedVenue) return;
      setSelectedVenue(null);
      setRestoreBubbleVenue(null);
      setVenueReturnToEvents(false);
      setSheet('search');
    }
  };

  const openVenueSheet = (venue: Venue) => {
    setSheet(null);
    setSearchQuery("");
    setRestoreBubbleVenue(null);
    setVenueReturnToEvents(false);
    setSelectedVenue(venue);
  };

  // Notifications inbox is an overlay sheet (same slot as Profile/Search so only
  // one is open at a time). Re-tapping the active Bell toggles back to the Map.
  const openNotifications = () => {
    if (sheet === 'notifications') {
      resetToMap();
      return;
    }
    setSelectedVenue(null);
    setRestoreBubbleVenue(null);
    setVenueReturnToEvents(false);
    setSearchQuery("");
    setSheet('notifications');
  };

  // Tapping a notification: close the inbox, then follow its deep-link (if any).
  const handleNotificationSelect = (link: string) => {
    setSheet(null);
    if (link) setLocation(link);
  };

  // v4-style nav handler: Map/Nearby/Events swap pages; Profile opens as an overlay
  // sheet that sits on top of whichever page is showing (with backdrop + slide-up animation).
  // Re-tapping the currently-active non-Map button (Nearby/Events/Profile) toggles
  // back to the Map. Tapping Map while already on Map is a no-op.
  const handleNav = (id: 'map' | 'nearby' | 'events' | 'profile') => {
    if (id === 'profile') {
      openSheet('profile');
      return;
    }
    // Re-tapping the currently-active non-Map button toggles back to the Map.
    // Tapping Map while already on Map remains a no-op.
    if (nav === id) {
      if (id === 'map') {
        if (sheet === null && !selectedVenue) return;
        resetToMap();
        return;
      }
      resetToMap();
      return;
    }
    setNav(id);
    setSheet(null);
    setSelectedVenue(null);
    setRestoreBubbleVenue(null);
    setVenueReturnToEvents(false);
    setSearchQuery("");
  };

  // Returns the nav to the map and closes any open page overlays (Nearby/Events/
  // Profile), search panel, and selected venue. Callers can pass a callback to
  // synchronously open something else (e.g. a venue detail). React batches these
  // setState calls within an event handler, so the callback's setSelectedVenue(venue)
  // wins over the setSelectedVenue(null) above it.
  const resetToMap = (callback?: () => void) => {
    setNav('map');
    setSheet(null);
    setSelectedVenue(null);
    setRestoreBubbleVenue(null);
    setVenueReturnToEvents(false);
    setSearchQuery("");
    callback?.();
  };

  // Swipe-down dismissal for full-screen overlays (Explore/Events/Profile).
  // Each overlay has its own DragControls so we can attach the gesture only to
  // a top "drag handle" region — keeping inner scrollable content fully usable.
  const nearbyDragControls = useDragControls();
  const eventsDragControls = useDragControls();
  const profileDragControls = useDragControls();
  const notificationsDragControls = useDragControls();
  const settingsDragControls = useDragControls();
  const SWIPE_DOWN_DISTANCE = 110;
  const SWIPE_DOWN_VELOCITY = 600;
  const handleSwipeDownDismiss = (_e: unknown, info: PanInfo, onDismiss: () => void) => {
    if (info.offset.y > SWIPE_DOWN_DISTANCE || info.velocity.y > SWIPE_DOWN_VELOCITY) {
      onDismiss();
    }
  };

  const headerHidden = nav !== 'map' || !!selectedVenue || searchOpen || sheet === 'profile';

  return (
    <div className="h-screen w-full bg-background relative overflow-hidden">
      {/* Search Header Overlay */}
      <div className={`absolute top-0 left-0 right-0 z-[1000] p-4 bg-gradient-to-b from-background/95 via-background/60 to-transparent pt-safe transition-all duration-300 ease-in-out ${headerHidden ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}>
        <div className="max-w-md mx-auto flex flex-col gap-2 mt-[55px]">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="input-search-trigger"
                placeholder="Find bars, pubs..."
                className="pl-10 bg-card border-border text-foreground placeholder:text-zinc-500 shadow-lg rounded-full cursor-pointer"
                readOnly
                onClick={() => openSheet('search')}
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full bg-card border-0 text-zinc-500 hover:bg-card hover:text-white shadow-lg gap-2 flex-shrink-0"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5 text-zinc-500" />
                  Filter
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3 rounded-2xl shadow-xl bg-card border border-border text-foreground" align="end">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-zinc-500 px-1">VENUE TYPE</p>
                    <div className="flex flex-wrap gap-1.5">
                      {VENUE_TYPES.map((type) => (
                        <button
                          key={type}
                          data-testid={`filter-type-${type.toLowerCase()}`}
                          onClick={() => toggleTypeFilter(type)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                            typeFilters.has(type)
                              ? "bg-primary text-primary-foreground font-medium"
                              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                          }`}
                        >
                          {type}
                          {typeFilters.has(type) && <Check className="w-3 h-3" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-zinc-500 px-1">STATUS</p>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        data-testid="filter-show-closed"
                        onClick={() => setShowClosed(!showClosed)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                          showClosed
                            ? "bg-zinc-700 text-zinc-100 font-medium"
                            : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                        }`}
                      >
                        Closed
                        {showClosed && <Check className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-zinc-500 px-1">VIBE / CROWD</p>
                    <div className="flex flex-wrap gap-1.5">
                      {VIBE_TYPES.map((vibe) => (
                        <button
                          key={vibe}
                          data-testid={`filter-vibe-${vibe.toLowerCase()}`}
                          onClick={() => toggleVibeFilter(vibe)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                            vibeFilters.has(vibe)
                              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-medium"
                              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                          }`}
                        >
                          {vibe}
                          {vibeFilters.has(vibe) && <Check className="w-3 h-3" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  {activeFiltersCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-xs text-zinc-400 hover:text-white hover:bg-white/5"
                      onClick={() => {
                        setTypeFilters(new Set());
                        setVibeFilters(new Set());
                        setShowClosed(false);
                      }}
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {activeFiltersCount > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {showClosed && (
                <Badge 
                  key="show-closed"
                  variant="secondary" 
                  className="rounded-full px-3 py-1 bg-zinc-700 text-zinc-100 border-zinc-600 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  onClick={() => setShowClosed(false)}
                >
                  Closed
                  <span className="text-xs opacity-60">×</span>
                </Badge>
              )}
              {Array.from(typeFilters).map(type => (
                <Badge 
                  key={`type-${type}`}
                  variant="secondary" 
                  className="rounded-full px-3 py-1 bg-primary/15 text-primary border-primary/30 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  onClick={() => toggleTypeFilter(type)}
                >
                  {type}
                  <span className="text-xs opacity-60">×</span>
                </Badge>
              ))}
              {Array.from(vibeFilters).map(vibe => (
                <Badge 
                  key={`vibe-${vibe}`}
                  variant="secondary" 
                  className="rounded-full px-3 py-1 bg-emerald-500/15 text-emerald-300 border-emerald-500/30 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  onClick={() => toggleVibeFilter(vibe)}
                >
                  {vibe}
                  <span className="text-xs opacity-60">×</span>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Map */}
      <MapContainer 
        center={mapCenter} 
        zoom={11} 
        style={{ height: "100%", width: "100%", background: "#0F0F0F" }}
        className="z-0"
        zoomControl={false}
        attributionControl={false}
        {...MAP_ROTATE_OPTIONS}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_API_KEY)}`}
        />
        
        {/* Recenter helper */}
        <RecenterMap lat={mapCenter[0]} lng={mapCenter[1]} zoom={currentZoom} version={recenterVersion} onComplete={handleRecenterComplete} />

        {/* Viewport bounds tracker — feeds mapBounds state for scoped venue fetch */}
        <MapBoundsTracker onBoundsChange={setMapBounds} onZoomChange={setCurrentZoom} />

        {/* Re-shows the closed venue's name/capacity bubble back on the map */}
        <RestoreVenueBubble
          venue={restoreBubbleVenue}
          onOpen={openVenueSheet}
          onClear={() => setRestoreBubbleVenue(null)}
        />

        {/* Map-anchored baked heatmap (scales/rotates with the map, no redraw on view change) */}
        <BakedHeatLayer venues={heatFilteredVenues} />

        {/* Always-on device-facing direction cone; compensates for map rotation */}
        <MapBearingSync onBearingChange={handleBearingChange} resetVersion={resetBearingVersion} />
        {showHeadingCone && userLocation && (
          <UserHeadingCone position={userLocation} rotation={coneRotation} />
        )}

        {/* User Location Marker */}
        {userLocation && (
          <MapMarker 
            venue={{ 
              id: -1, 
              name: "You", 
              type: "user", 
              description: "", 
              latitude: userLocation[0], 
              longitude: userLocation[1], 
              capacityLevel: 0, 
              temperature: 50,
              reportCount: 0,
              isSponsored: false,
              lastUpdated: new Date(),
              googlePlaceId: null,
              googleRating: null,
              googleRatingCount: null,
              googlePriceLevel: null,
              openingHours: null,
              instagramUrl: null,
              tiktokUrl: null,
              websiteUrl: null,
              logoUrl: null,
              coverPhotoUrl: null,
              noPhotosAt: null,
              noPhotosCount: 0,
              noHistogramAt: null,
              noHistogramCount: 0,
              street: null,
              postcode: null
            }}
            onClick={() => {}}
            onOpenDrawer={() => {}}
            zoom={currentZoom}
          />
        )}

        {/* Sponsored venue markers — visible at all zoom levels.
            Business users only see Honey markers for their own venues. */}
        <SponsoredVenueMarkers
          venues={sponsoredVenueMarkers}
          onVenueOpen={openVenueSheet}
          isBusinessUser={isBusinessUser}
          ownedVenueIds={ownedVenueIds}
        />

        {/* Owned venue markers for business users — always visible regardless of zoom, with amber highlight ring.
            Sourced from /api/business/my-venues (the full owned-venue list, not viewport-scoped) so they don't
            disappear when /api/venues' bounds-scoped LIMIT 500 excludes them at low zoom. Live capacity / colour
            are merged in from the bounds-scoped venues response when available. */}
        {isBusinessUser && ownedVenueMarkers.length > 0 && (
          <ClusteredVenueMarkers
            venues={ownedVenueMarkers}
            onVenueOpen={openVenueSheet}
            highlightOwned
          />
        )}

        {/* Venue markers — only at Z16+ so heatmap is the focus below that.
            For business users, exclude their own venues here (they're rendered above at all zooms). */}
        {currentZoom >= 15.5 && (
          <ClusteredVenueMarkers
            venues={isBusinessUser ? filteredVenues.filter(v => !ownedVenueIds.has(v.id)) : filteredVenues}
            onVenueOpen={openVenueSheet}
          />
        )}

        {/* Friend map pins */}
        {user && currentZoom >= 13 && (
          <FriendPins
            venues={venues}
            onVenueOpen={(venue) => {
              requestRecenter([venue.latitude, venue.longitude], 16);
              openVenueSheet(venue);
            }}
          />
        )}
      </MapContainer>
      {/* Motion-access hint — centred overlay above map and nav */}
      {showCompassHint && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-6 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-2 pl-3 pr-2 py-2 rounded-full glass-card border-white/10 shadow-lg">
            <button
              data-testid="button-enable-compass"
              onClick={() => enableHeading()}
              className="flex items-center gap-2 text-sm text-white"
            >
              <Compass className="w-4 h-4 text-primary" />
              <span>Show your direction</span>
            </button>
            <button
              data-testid="button-dismiss-compass"
              onClick={() => setCompassHintDismissed(true)}
              aria-label="Dismiss"
              className="p-1 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Navigation Bar + Recenter aligned to search bar bounds */}
      <div className="fixed bottom-0 left-0 right-0 z-[1000]">
        <div className="max-w-md mx-auto px-4 pb-6 relative">
          <div className="pr-14">
            <Navigation
              active={sheet === 'profile' ? 'profile' : sheet === 'notifications' ? 'notifications' : nav}
              onMapClick={() => handleNav('map')}
              onNearbyClick={() => handleNav('nearby')}
              onAccountClick={() => openSheet('profile')}
              onNotificationsClick={openNotifications}
              onEventsClick={() => handleNav('events')}
            />
          </div>
          <div className="absolute bottom-6 right-4 flex flex-col gap-2 items-center p-2 rounded-full glass-card border-white/10 scale-90 origin-bottom-right">
            <button
              data-testid="recenter-button"
              onClick={() => {
                setResetBearingVersion((v) => v + 1);
                if (!("geolocation" in navigator)) return;
                setIsRecentering(true);
                geoResolvedRef.current = false;
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
                    setUserLocation(coords);
                    requestRecenter(coords, 16);
                  },
                  () => {
                    dismissOverlay();
                  },
                  { enableHighAccuracy: true, timeout: 10000 }
                );
              }}
              className="p-3 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all duration-300"
            >
              <NavIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* Detail Drawer */}
      <VenueDetailDrawer 
        venue={selectedVenue} 
        isOpen={!!selectedVenue} 
        onClose={() => {
          const closed = selectedVenue;
          setSelectedVenue(null);
          // Opened from the Events page: return there instead of the map,
          // and skip the map restore-bubble.
          if (venueReturnToEvents) {
            setVenueReturnToEvents(false);
            setNav('events');
            return;
          }
          if (closed) setRestoreBubbleVenue(closed);
        }}
        onUserClick={(uid) => openUserVibes(uid, "venueDetail")}
      />
      {/* Nearby Page — full-screen page swap (v4 nav model) */}
      <AnimatePresence>
        {nav === 'nearby' && (
          <motion.div
            className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
            initial={PAGE_SWAP_TRANSITION.initial}
            animate={PAGE_SWAP_TRANSITION.animate}
            exit={PAGE_SWAP_TRANSITION.exit}
            transition={PAGE_SWAP_TRANSITION.transition}
            drag="y"
            dragListener={false}
            dragControls={nearbyDragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragMomentum={false}
            onDragEnd={(e, info) => handleSwipeDownDismiss(e, info, () => resetToMap())}
          >
          <div className="flex flex-col h-full">
            <div className="px-4 pt-5 pb-3 flex-shrink-0">
              <div
                className="flex items-center justify-between mb-3 touch-none"
                onPointerDown={(e) => nearbyDragControls.start(e)}
                data-testid="drag-handle-nearby"
              >
                <h2 className="font-display text-white text-[28px] font-black" data-testid="text-nearby-title">Explore</h2>
                <Button
                  data-testid="button-close-nearby"
                  variant="outline"
                  size="icon"
                  className="rounded-full border-zinc-700 text-zinc-400"
                  onClick={() => setNav('map')}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="relative mb-3">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="input-nearby-search"
                  placeholder="Search venues, areas…"
                  className="pl-10 pr-9 bg-zinc-900 border-zinc-800 text-white rounded-full h-11"
                  value={nearbySearch}
                  onChange={(e) => setNearbySearch(e.target.value)}
                />
                {nearbySearch && (
                  <button
                    data-testid="button-nearby-search-clear"
                    onClick={() => setNearbySearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-zinc-500 hover:text-zinc-300 text-xl leading-none"
                  >×</button>
                )}
              </div>

              <div className="flex gap-1.5">
                {(["discover", "vibes", "events"] as const).map(t => (
                  <button
                    key={t}
                    data-testid={`nearby-tab-${t}`}
                    onClick={() => { setNearbyTab(t); setNearbySearch(""); }}
                    className={`flex-1 text-center py-2 rounded-xl text-[13px] font-bold transition-colors ${nearbyTab === t ? "bg-primary text-primary-foreground" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}
                  >
                    {t === "discover" ? "Discover" : t === "vibes" ? "Vibes" : "Events"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-8 pt-2">
              {nearbySearch.trim() ? (
                <div>
                  {nearbySearchNeighbourhoods.length > 0 && (
                    <>
                      <p className="text-[11px] font-bold text-zinc-500 tracking-widest mb-2.5 mt-1">AREAS</p>
                      <div className="space-y-2 mb-4">
                        {nearbySearchNeighbourhoods.map(n => (
                          <div
                            key={n.name}
                            data-testid={`nearby-area-result-${n.name.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => {
                              setNav('map');
                              setNearbySearch("");
                              requestRecenter([n.lat, n.lng], 16);
                            }}
                            className="flex items-center gap-3 p-3.5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 cursor-pointer active:scale-[0.98] transition-all"
                          >
                            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                              <MapPin className="w-4 h-4 text-primary" />
                            </div>
                            <span className="text-white font-bold text-[15px] flex-1">{n.name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {nearbySearchVenues.length > 0 && (
                    <>
                      <p className="text-[11px] font-bold text-zinc-500 tracking-widest mb-2.5">VENUES</p>
                      <div className="space-y-2">
                        {nearbySearchVenues.map(venue => (
                          <NearbyVenueRow
                            key={venue.id}
                            venue={venue}
                            onTap={() => {
                              requestRecenter([venue.latitude, venue.longitude], 16);
                              resetToMap(() => setSelectedVenue(venue));
                            }}
                            getCapacityBg={getCapacityBg}
                            getCapacityColor={getCapacityColor}
                            getTypeIcon={getTypeIcon}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {nearbySearchVenues.length === 0 && nearbySearchNeighbourhoods.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-3xl mb-2">🔍</p>
                      <p className="text-zinc-300 font-bold text-[15px] mb-1" data-testid="text-nearby-no-results">No results</p>
                      <p className="text-zinc-500 text-[13px]">Try a different venue or area</p>
                    </div>
                  )}
                </div>
              ) : nearbyTab === "discover" ? (
                <div>
                  {isLoading && (venues ?? []).length === 0 ? (
                    <div className="space-y-3 pt-2">
                      {Array(4).fill(0).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 p-3.5 rounded-2xl bg-zinc-900 border border-zinc-800 animate-pulse">
                          <div className="w-12 h-12 rounded-full bg-zinc-800" />
                          <div className="flex-1 space-y-2"><div className="h-3 bg-zinc-800 rounded w-1/3" /><div className="h-3 bg-zinc-800 rounded w-1/2" /></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {hotVenues.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                              <Flame className="w-4 h-4 text-orange-400" />
                              <span className="text-white font-extrabold text-[15px]">Hot Right Now</span>
                            </div>
                          </div>
                          <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {hotVenues.map(v => {
                              const cap = (v as any).capacityPercent ?? 0;
                              const colour = getCapacityColour((v as any).capacityColour ?? "blue");
                              return (
                                <div
                                  key={v.id}
                                  data-testid={`nearby-hot-card-${v.id}`}
                                  onClick={() => {
                                    requestRecenter([v.latitude, v.longitude], 16);
                                    resetToMap(() => setSelectedVenue(v));
                                  }}
                                  className="flex-shrink-0 w-[150px] rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 cursor-pointer active:scale-[0.98] transition-all"
                                >
                                  <div className="relative h-20 bg-zinc-800 overflow-hidden">
                                    {(v as any).coverPhotoUrl ? (
                                      <img src={(v as any).coverPhotoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                                    ) : (
                                      <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,#1f1f1f_0,#1f1f1f_4px,#272727_4px,#272727_10px)]" />
                                    )}
                                    <div
                                      className="absolute top-1.5 right-1.5 rounded-md px-1.5 py-0.5 text-[10px] font-extrabold border"
                                      style={{ background: `${colour}22`, borderColor: `${colour}55`, color: colour }}
                                    >{cap}%</div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/90 to-transparent" />
                                  </div>
                                  <div className="px-2.5 py-2.5">
                                    <p className="text-[13px] font-extrabold text-white truncate">{v.name}</p>
                                    <p className="text-[11px] text-zinc-500 truncate">{venueArea(v) ?? v.type}</p>
                                    <div className="flex items-center gap-1 mt-1.5">
                                      <span className={`w-1.5 h-1.5 rounded-full ${getCapacityLabelDotClass(cap)}`} />
                                      <span className={`text-[11px] font-semibold ${getCapacityLabelColor(cap)}`}>{getCapacityLabel(cap)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {browseAreas.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center gap-1.5 mb-3">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span className="text-white font-extrabold text-[15px]">Browse by Area</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {browseAreas.map(area => {
                              const busiest = [...area.venues].sort((a, b) => ((b as any).capacityPercent ?? 0) - ((a as any).capacityPercent ?? 0))[0];
                              const cap = (busiest as any)?.capacityPercent ?? 0;
                              return (
                                <div
                                  key={area.name}
                                  data-testid={`nearby-area-${area.name.toLowerCase().replace(/\s+/g, "-")}`}
                                  onClick={() => {
                                    setNav('map');
                                    requestRecenter([area.lat, area.lng], 15);
                                  }}
                                  className="rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 px-3.5 py-3 cursor-pointer active:scale-[0.98] transition-all"
                                >
                                  <p className="text-[14px] font-extrabold text-white mb-0.5">{area.name}</p>
                                  <p className="text-[11px] text-zinc-500 mb-1.5">{area.venues.length} venue{area.venues.length !== 1 ? "s" : ""}</p>
                                  <div className="flex items-center gap-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${getCapacityLabelDotClass(cap)}`} />
                                    <span className={`text-[10px] font-semibold ${getCapacityLabelColor(cap)}`}>{getCapacityLabel(cap)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {quietVenues.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center gap-1.5 mb-3">
                            <span className="text-base">😌</span>
                            <span className="text-white font-extrabold text-[15px]">Quiet Gems</span>
                          </div>
                          <div className="space-y-2">
                            {quietVenues.map(v => (
                              <NearbyVenueRow
                                key={v.id}
                                venue={v}
                                onTap={() => {
                                  requestRecenter([v.latitude, v.longitude], 16);
                                  resetToMap(() => setSelectedVenue(v));
                                }}
                                getCapacityBg={getCapacityBg}
                                getCapacityColor={getCapacityColor}
                                getTypeIcon={getTypeIcon}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 mb-3">
                        <span className="text-base">🍺</span>
                        <span className="text-white font-extrabold text-[15px]">All Venues</span>
                      </div>
                      <div className="space-y-2">
                        {nearbyVenues.length === 0 ? (
                          <p className="text-zinc-600 text-center py-6">No venues found.</p>
                        ) : (
                          nearbyVenues.map(venue => (
                            <NearbyVenueRow
                              key={venue.id}
                              venue={venue}
                              dist={venue.distance}
                              onTap={() => {
                                requestRecenter([venue.latitude, venue.longitude], 16);
                                resetToMap(() => setSelectedVenue(venue));
                              }}
                              getCapacityBg={getCapacityBg}
                              getCapacityColor={getCapacityColor}
                              getTypeIcon={getTypeIcon}
                            />
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : nearbyTab === "vibes" ? (
                <div className="space-y-2.5">
                  {userVibesLoading ? (
                    Array(4).fill(0).map((_, i) => (
                      <div key={i} className="flex gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800 animate-pulse">
                        <div className="w-16 h-14 rounded-xl bg-zinc-800 flex-shrink-0" />
                        <div className="flex-1 space-y-2 pt-1"><div className="h-3 bg-zinc-800 rounded w-2/3" /><div className="h-3 bg-zinc-800 rounded w-1/3" /></div>
                      </div>
                    ))
                  ) : (userVibesData?.vibes ?? []).length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-3xl mb-2">✨</p>
                      <p className="text-zinc-300 font-bold text-[15px] mb-1" data-testid="text-nearby-vibes-empty">No vibes yet</p>
                      <p className="text-zinc-500 text-[13px]">Share a photo or video at a venue</p>
                    </div>
                  ) : (
                    (userVibesData?.vibes ?? []).map(item => {
                      const thumb = item.mediaType === "video" ? item.thumbnailUrl : item.cloudinaryUrl;
                      const subtitle = item.vibe
                        ? item.vibe
                        : (item.mediaType === "video" ? "Video" : "Photo");
                      const openViewer = () => {
                        // Construct FeedMediaItem explicitly with safe defaults —
                        // /api/vibes/explore returns a slimmer shape than FeedMediaItem,
                        // and we deliberately bypass openFeedMedia() so closing the
                        // viewer returns the user to the Nearby > Vibes tab (which
                        // is still mounted underneath) instead of opening Search.
                        const asFeed: FeedMediaItem = {
                          id: item.id,
                          venueId: item.venueId,
                          venueName: item.venueName,
                          userId: item.userId,
                          username: item.username,
                          mediaType: item.mediaType ?? "photo",
                          cloudinaryUrl: item.cloudinaryUrl ?? "",
                          thumbnailUrl: item.thumbnailUrl ?? null,
                          durationSeconds: null,
                          thumbsUp: 0,
                          thumbsDown: 0,
                          createdAt: item.date,
                        };
                        setVibeReturnToSearch(false);
                        setSelectedFeedMedia(asFeed);
                        setViewerCounts(c => ({ ...c, [item.id]: { thumbsUp: 0, thumbsDown: 0 } }));
                      };
                      return (
                        <div
                          key={`${item.type}-${item.id}`}
                          role="button"
                          tabIndex={0}
                          data-testid={`nearby-vibe-${item.type}-${item.id}`}
                          onClick={openViewer}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openViewer(); } }}
                          className="flex gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800 w-full text-left cursor-pointer active:opacity-80 transition-opacity"
                        >
                          <div
                            data-testid={`nearby-vibe-thumb-${item.id}`}
                            className="relative w-16 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-zinc-800"
                          >
                            {thumb ? (
                              <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="w-5 h-5 text-zinc-600" />
                              </div>
                            )}
                            {item.mediaType === "video" && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center">
                                  <Play className="w-3 h-3 text-white fill-white ml-0.5" />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-zinc-300 truncate"><span className="font-bold text-white">{item.venueName}</span></p>
                            <button
                              type="button"
                              data-testid={`nearby-vibe-user-${item.userId}-${item.id}`}
                              onClick={(e) => { e.stopPropagation(); openUserVibes(item.userId, "search"); }}
                              className="flex items-center gap-1.5 mt-0.5 group cursor-pointer"
                            >
                              {item.profilePhotoUrl ? (
                                <img src={item.profilePhotoUrl} alt="" className="w-4 h-4 rounded-full object-cover border border-zinc-700" />
                              ) : (
                                <div className="w-4 h-4 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                                  <span className="text-[8px] font-bold text-white">{(item.username || "?")[0]?.toUpperCase()}</span>
                                </div>
                              )}
                              <span className="text-[11px] text-zinc-400 group-hover:text-zinc-200 truncate">@{item.username}</span>
                              <span className="text-[11px] text-zinc-600">· {timeAgo(item.date)}</span>
                            </button>
                            <p className="text-[12px] text-zinc-400 mt-0.5 truncate">{subtitle}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div>
                  <div className="bg-zinc-900 rounded-2xl p-1 flex gap-0.5 mb-3.5">
                    {(["tonight", "this_week"] as const).map(f => (
                      <button
                        key={f}
                        data-testid={`nearby-event-filter-${f}`}
                        onClick={() => setEventFilter(f)}
                        className={`flex-1 text-center py-2.5 rounded-xl text-[13px] font-bold transition-colors ${eventFilter === f ? "bg-primary text-primary-foreground" : "text-zinc-300 hover:text-white"}`}
                      >
                        {f === "tonight" ? "Tonight 🔥" : "This Week"}
                      </button>
                    ))}
                  </div>
                  <GenreChipRow
                    selected={eventGenre}
                    onSelect={setEventGenre}
                    testIdPrefix="nearby-event-genre"
                  />
                  <EventsListSection
                    events={(eventsData ?? []) as EventListItem[]}
                    loading={eventsLoading}
                    genre={eventGenre}
                    userLat={userLat}
                    userLng={userLng}
                    onTap={handleEventTap}
                    testIdPrefix="nearby-event-card"
                    emptyTitle={eventFilter === "tonight" ? "Nothing on tonight" : "Nothing on this week"}
                    emptySubtitle={eventFilter === "tonight" ? "Check back later or browse this week" : "Try a different genre"}
                  />
                </div>
              )}
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Search Page — full-screen page swap (v4 nav model, peer of Nearby/Events) */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
            data-testid="sheet-search"
            initial={PAGE_SWAP_TRANSITION.initial}
            animate={PAGE_SWAP_TRANSITION.animate}
            exit={PAGE_SWAP_TRANSITION.exit}
            transition={PAGE_SWAP_TRANSITION.transition}
          >
          <div className="flex flex-col h-full">
            <div className="px-6 pt-6 pb-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-white text-[28px] font-black" data-testid="text-search-title">Search</h2>
                <Button
                  data-testid="button-close-search"
                  variant="outline"
                  size="icon"
                  className="rounded-full border-zinc-700 text-zinc-400"
                  onClick={() => { setSheet(null); setSearchQuery(""); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-zinc-500 text-sm mb-4">All bars, pubs and clubs in London</p>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  data-testid="input-search-query"
                  placeholder="Search venues or @username..."
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white rounded-xl"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value.startsWith("@")) setViewingUsername(null); }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-8">
              {/* @-username search */}
              {searchQuery.startsWith("@") && !viewingUsername && (
                <div className="space-y-3">
                  <p className="text-xs text-zinc-500 mb-3">Search by username</p>
                  {searchQuery.length <= 1 ? (
                    <p className="text-zinc-600 text-center py-8">Type a username to search</p>
                  ) : userSearchLoading ? (
                    Array(3).fill(0).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-zinc-800" />
                        <div className="h-3 bg-zinc-800 rounded w-1/3" />
                      </div>
                    ))
                  ) : (userSearchData?.users ?? []).length === 0 ? (
                    <p className="text-zinc-600 text-center py-8">No users found</p>
                  ) : (
                    (userSearchData?.users ?? []).map(u => (
                      <div
                        key={u.id}
                        data-testid={`user-result-${u.id}`}
                        className="flex items-center gap-3 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 cursor-pointer active:scale-[0.98] transition-all"
                        onClick={() => setViewingUsername(u.username)}
                      >
                        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-zinc-400" />
                        </div>
                        <span className="text-white font-medium">@{u.username}</span>
                        <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* User media grid */}
              {searchQuery.startsWith("@") && viewingUsername && (
                <div>
                  <button
                    className="flex items-center gap-2 text-zinc-400 text-sm mb-4 hover:text-white transition-colors"
                    onClick={() => setViewingUsername(null)}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    @{viewingUsername}
                  </button>
                  {userMediaLoading ? (
                    <div className="grid grid-cols-3 gap-2">
                      {Array(6).fill(0).map((_, i) => <div key={i} className="aspect-square rounded-xl bg-zinc-800 animate-pulse" />)}
                    </div>
                  ) : (userMediaData?.media ?? []).length === 0 ? (
                    <p className="text-zinc-600 text-center py-12">No media yet</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(userMediaData?.media ?? []).map(item => {
                        const thumb = item.mediaType === "video" ? item.thumbnailUrl : item.cloudinaryUrl;
                        return (
                          <button
                            key={item.id}
                            data-testid={`user-media-grid-${item.id}`}
                            className="relative aspect-square rounded-xl overflow-hidden bg-zinc-800 focus:outline-none"
                            onClick={() => openUserMediaItem(item)}
                          >
                            {thumb ? (
                              <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><Video className="w-6 h-6 text-zinc-600" /></div>
                            )}
                            {item.mediaType === "video" && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-7 h-7 rounded-full bg-black/60 border border-white/30 flex items-center justify-center">
                                  <Play className="w-3 h-3 text-white fill-white ml-0.5" />
                                </div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Existing venue search — only when not in feed or @ mode */}
              {!searchQuery.startsWith("@") && searchQuery !== "" && (
                <div className="space-y-3">
                  {isLoading ? (
                    Array(5).fill(0).map((_, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
                        <Skeleton className="w-12 h-12 rounded-full bg-zinc-800" />
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-4 w-1/3 bg-zinc-800" />
                          <Skeleton className="h-3 w-1/2 bg-zinc-800" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      {searchNeighbourhoods.length > 0 && (
                        <div className="space-y-2 mb-4">
                          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Neighbourhoods</p>
                          {searchNeighbourhoods.map(n => (
                            <div
                              key={n.name}
                              data-testid={`neighbourhood-result-${n.name.toLowerCase().replace(/\s+/g, "-")}`}
                              onClick={() => {
                                setSheet(null);
                                setSearchQuery("");
                                requestRecenter([n.lat, n.lng], 16);
                              }}
                              className="flex items-center gap-3 p-4 rounded-2xl bg-primary/10 border border-primary/20 hover:border-primary/40 transition-all cursor-pointer active:scale-[0.98]"
                            >
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                                <MapPin className="w-5 h-5 text-primary" />
                              </div>
                              <span className="text-white font-display font-bold text-base">{n.name}</span>
                              <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
                            </div>
                          ))}
                        </div>
                      )}
                      {(searchNeighbourhoods.length > 0 ? neighbourhoodVenues : searchVenues).length === 0 ? (
                        <p className="text-zinc-600 text-center py-8">No venues match your search.</p>
                      ) : (
                        (searchNeighbourhoods.length > 0 ? neighbourhoodVenues : searchVenues).map(venue => {
                          const street = (venue as any).street as string | null | undefined;
                          const postcode = (venue as any).postcode as string | null | undefined;
                          const area = venueArea(venue);
                          const locationParts = [street, postcode, area].filter(Boolean) as string[];
                          const locationLine = locationParts.length > 0
                            ? locationParts.join(" · ")
                            : (venue.description || venue.type);
                          return (
                          <div
                            key={venue.id}
                            data-testid={`search-venue-${venue.id}`}
                            onClick={() => {
                              requestRecenter([venue.latitude, venue.longitude], 16);
                              resetToMap(() => setSelectedVenue(venue));
                            }}
                            className={`group relative isolate overflow-hidden flex items-center gap-4 p-4 rounded-2xl border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all cursor-pointer active:scale-[0.98] ${(venue as any).coverPhotoUrl ? "bg-zinc-900/80" : "bg-zinc-900"}`}
                          >
                            <CardPhotoBg url={(venue as any).coverPhotoUrl} />
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0 ${getCapacityBg((venue as any).capacityPercent ?? 0)}`}>
                              <span className={`text-sm font-bold ${getCapacityColor((venue as any).capacityPercent ?? 0)}`}>
                                {(venue as any).capacityPercent ?? 0}%
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <h3 className="font-display font-bold text-white text-base truncate">{venue.name}</h3>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {(venue as any).isOpen === false && (
                                    <Badge
                                      variant="outline"
                                      data-testid={`badge-closed-${venue.id}`}
                                      className="border-zinc-700 bg-zinc-900/80 text-[10px] text-zinc-400"
                                    >
                                      Closed
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="border-white/10 text-[10px] text-zinc-500 capitalize">
                                    {venue.type}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center text-zinc-500 text-sm truncate">
                                <MapPin className="w-3 h-3 mr-1 inline shrink-0" />
                                <span className="truncate" data-testid={`text-location-${venue.id}`}>{locationLine}</span>
                              </div>
                            </div>
                            <div className="text-zinc-600 shrink-0">
                              {getTypeIcon(venue.type, venue.isSponsored)}
                            </div>
                          </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Events Page — full-screen page swap (v4 nav model) */}
      <AnimatePresence>
        {nav === 'events' && (
          <motion.div
            className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
            initial={PAGE_SWAP_TRANSITION.initial}
            animate={PAGE_SWAP_TRANSITION.animate}
            exit={PAGE_SWAP_TRANSITION.exit}
            transition={PAGE_SWAP_TRANSITION.transition}
            drag="y"
            dragListener={false}
            dragControls={eventsDragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragMomentum={false}
            onDragEnd={(e, info) => handleSwipeDownDismiss(e, info, () => resetToMap())}
          >
          <div className="flex flex-col h-full">
            <div className="px-5 pt-4 pb-2 flex-shrink-0">
              <div
                className="flex items-center justify-between mb-1 touch-none"
                onPointerDown={(e) => eventsDragControls.start(e)}
                data-testid="drag-handle-events"
              >
                <h2 className="text-[28px] font-display font-black text-white tracking-tight" data-testid="text-events-title">Events</h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-1.5">
                    <MapPin className="w-3 h-3 text-primary" />
                    <span className="text-[13px] font-semibold text-zinc-300">London</span>
                  </div>
                  <Button
                    data-testid="button-close-events"
                    variant="outline"
                    size="icon"
                    className="rounded-full border-zinc-700 text-zinc-400 h-9 w-9"
                    onClick={() => setNav('map')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-[13px] text-zinc-500 mb-3.5">What's on tonight and this week</p>

              <div className="bg-zinc-900 rounded-2xl p-1 flex gap-0.5 mb-3.5">
                {(["tonight", "this_week"] as const).map(f => (
                  <button
                    key={f}
                    data-testid={`event-filter-${f}`}
                    onClick={() => setEventFilter(f)}
                    className={`flex-1 text-center py-2.5 rounded-xl text-[13px] font-bold transition-colors ${eventFilter === f ? "bg-primary text-primary-foreground" : "text-zinc-300 hover:text-white"}`}
                  >
                    {f === "tonight" ? "Tonight 🔥" : "This Week"}
                  </button>
                ))}
              </div>

              <GenreChipRow
                selected={eventGenre}
                onSelect={setEventGenre}
                testIdPrefix="event-genre"
              />
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-8">
              <EventsListSection
                events={(eventsData ?? []) as EventListItem[]}
                loading={eventsLoading}
                genre={eventGenre}
                userLat={userLat}
                userLng={userLng}
                onTap={handleEventTap}
                testIdPrefix="event-card"
                emptyTitle={eventFilter === "tonight" ? "Nothing on tonight" : "Nothing on this week"}
                emptySubtitle={eventFilter === "tonight" ? "Check back later or browse this week" : "Try a different genre"}
              />
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Profile Page — full-screen page swap (peer of Nearby/Events/Search) */}
      <AnimatePresence>
        {sheet === 'profile' && (
        <motion.div
          className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
          data-testid="sheet-profile"
          initial={PAGE_SWAP_TRANSITION.initial}
          animate={PAGE_SWAP_TRANSITION.animate}
          exit={PAGE_SWAP_TRANSITION.exit}
          transition={PAGE_SWAP_TRANSITION.transition}
          drag="y"
          dragListener={false}
          dragControls={profileDragControls}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          dragMomentum={false}
          onDragEnd={(e, info) => handleSwipeDownDismiss(e, info, () => setSheet(null))}
        >
          <div
            className="flex justify-center pt-2 pb-1 flex-shrink-0 touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={(e) => profileDragControls.start(e)}
            data-testid="drag-handle-profile"
          >
            <div className="w-10 h-1 rounded-full bg-zinc-700" />
          </div>
          {user ? (
            <>
              <ProfilePage
                onClose={() => setSheet(null)}
                onVenueSelect={(v) => {
                  resetToMap(() => setSelectedVenue(v));
                  if (v.latitude && v.longitude) requestRecenter([v.latitude, v.longitude], 16);
                }}
                defaultTab={profileDefaultTab}
              />
              <div className="px-6 pb-6 flex-shrink-0 space-y-2">
                {user.role === "admin" && (
                  <button
                    data-testid="button-goto-admin"
                    onClick={() => { setSheet(null); setLocation("/admin"); }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left text-white"
                  >
                    <Shield className="w-5 h-5 text-violet-400 shrink-0" />
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">Admin Panel</p>
                      <p className="text-zinc-500 text-xs">Manage venues, users & sponsorships</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  </button>
                )}
                {(user.role === "admin" || user.role === "business") && (
                  <button
                    data-testid="button-goto-business"
                    onClick={() => { setSheet(null); setLocation("/business"); }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left text-white"
                  >
                    <Briefcase className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">Business Dashboard</p>
                      <p className="text-zinc-500 text-xs">Edit venues, events & view analytics</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  </button>
                )}
                <button
                  data-testid="button-goto-settings"
                  onClick={() => setSheet('settings')}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left text-white"
                >
                  <SettingsIcon className="w-5 h-5 text-zinc-300 shrink-0" />
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">Settings</p>
                    <p className="text-zinc-500 text-xs">Notifications, password, account</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                </button>
                <Button
                  data-testid="button-account-logout"
                  variant="outline"
                  className="w-full rounded-xl border-zinc-700 text-zinc-400 gap-2"
                  onClick={() => { logout.mutateAsync().then(() => setSheet(null)); }}
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </Button>
              </div>
            </>
          ) : (
            <div className="px-6 py-8 space-y-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-zinc-500" />
                </div>
                <h3 className="text-white font-display font-bold text-lg mb-1">Not signed in</h3>
                <p className="text-zinc-500 text-sm">Sign in to report crowd levels and contribute to the community.</p>
              </div>
              <Button
                data-testid="button-goto-login"
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-2"
                onClick={() => { setSheet(null); setLocation("/auth"); }}
              >
                <User className="w-4 h-4" />
                Sign In / Register
              </Button>
            </div>
          )}
        </motion.div>
        )}
        {sheet === 'notifications' && (
        <motion.div
          className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
          data-testid="sheet-notifications"
          initial={PAGE_SWAP_TRANSITION.initial}
          animate={PAGE_SWAP_TRANSITION.animate}
          exit={PAGE_SWAP_TRANSITION.exit}
          transition={PAGE_SWAP_TRANSITION.transition}
          drag="y"
          dragListener={false}
          dragControls={notificationsDragControls}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          dragMomentum={false}
          onDragEnd={(e, info) => handleSwipeDownDismiss(e, info, () => setSheet(null))}
        >
          <div
            className="flex justify-center pt-2 pb-1 flex-shrink-0 touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={(e) => notificationsDragControls.start(e)}
            data-testid="drag-handle-notifications"
          >
            <div className="w-10 h-1 rounded-full bg-zinc-700" />
          </div>
          {user ? (
            <NotificationsPanel onSelect={handleNotificationSelect} />
          ) : (
            <div className="px-6 py-8 space-y-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-zinc-500" />
                </div>
                <h3 className="text-white font-display font-bold text-lg mb-1">Not signed in</h3>
                <p className="text-zinc-500 text-sm">Sign in to see your notifications.</p>
              </div>
              <Button
                data-testid="button-notifications-goto-login"
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-2"
                onClick={() => { setSheet(null); setLocation("/auth"); }}
              >
                <User className="w-4 h-4" />
                Sign In / Register
              </Button>
            </div>
          )}
        </motion.div>
        )}
        {sheet === 'settings' && (
        <motion.div
          className="absolute inset-0 z-[500] bg-background flex flex-col pb-[95px] pt-[max(1rem,env(safe-area-inset-top))]"
          data-testid="sheet-settings"
          initial={PAGE_SWAP_TRANSITION.initial}
          animate={PAGE_SWAP_TRANSITION.animate}
          exit={PAGE_SWAP_TRANSITION.exit}
          transition={PAGE_SWAP_TRANSITION.transition}
          drag="y"
          dragListener={false}
          dragControls={settingsDragControls}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          dragMomentum={false}
          onDragEnd={(e, info) => handleSwipeDownDismiss(e, info, () => setSheet('profile'))}
        >
          <div
            className="flex justify-center pt-2 pb-1 flex-shrink-0 touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={(e) => settingsDragControls.start(e)}
            data-testid="drag-handle-settings"
          >
            <div className="w-10 h-1 rounded-full bg-zinc-700" />
          </div>
          {user ? (
            <SettingsPanel
              onClose={() => setSheet('profile')}
              onAccountDeleted={() => { setSheet(null); setLocation("/auth"); }}
            />
          ) : (
            <div className="px-6 py-8 space-y-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-zinc-500" />
                </div>
                <h3 className="text-white font-display font-bold text-lg mb-1">Not signed in</h3>
                <p className="text-zinc-500 text-sm">Sign in to manage your settings.</p>
              </div>
              <Button
                data-testid="button-settings-goto-login"
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-2"
                onClick={() => { setSheet(null); setLocation("/auth"); }}
              >
                <User className="w-4 h-4" />
                Sign In / Register
              </Button>
            </div>
          )}
        </motion.div>
        )}
      </AnimatePresence>
      {/* Full-screen media viewer (from feed / user grid) */}
      <FullscreenVibeViewer
        media={selectedFeedMedia}
        onClose={closeFeedMedia}
        onViewVenue={handleViewVenue}
        onUserClick={(uid) => openUserVibes(uid, "search")}
      />
      {/* Initial geolocation / Recenter overlay */}
      {(geolocating || isRecentering || overlayFading) && (
        <div
          className={`absolute inset-0 z-[2000] flex items-center justify-center bg-background/95 transition-opacity duration-500 ${overlayFading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <div className="flex flex-col items-center">
            <img
              src={beeLogo}
              alt="Buzzn"
              className="w-12 h-12 mb-4 animate-pulse drop-shadow-[0_0_18px_rgba(245,196,28,0.85)]"
            />
            <p className="text-white font-display font-bold text-lg">Locating the vibe maker</p>
            <p className="text-zinc-500 text-sm mt-1">Finding your location...</p>
          </div>
        </div>
      )}
    </div>
  );
}
